/**
 * RemainingPageSections.tsx
 * ─────────────────────────
 * All sections from S5 (90-Day Timeline) through to the footer.
 * Drop this after FarmstandSection in your page.
 */

import React, { useState } from "react";
import { useLpStatusDots } from "./lp-status-dots";
import { EditableText, EditableItem, RegenerateButton, EditableElement, LpReorderGroup, LpImageActions, LinkUrlPopover, useLpEditor, useLpMediaOverride, useLpV2 } from "./lp-editor";
import { CheckCircle2, Star, FlaskConical, Wheat, Droplet, Ban, Lock, Home, ShieldCheck, Sparkles, Loader2, RotateCw } from "lucide-react";
import { motion } from "framer-motion";
import { Marquee } from "@/components/Marquee";
import { FAQ } from "@/components/FAQ";
import { MediaPlaceholder, LpImage } from "@/components/LpMediaPlaceholder";
import { computeReceipt, type LpReceipt, type LpReviewDisplay } from "@/lib/lp-express-pricing";
import { ScienceSection, type ProofCopy } from "@/components/ScienceSection";

const CHECKOUT_URL = "https://thepapabear.com/apps/subscriptions/qc/1sQTsnfo";

/* Normalize the brand's proof points (jsonb of unknown shape) into a flat list
   of trim-clean strings for the trust-strip Marquee. Handles an array of
   strings, an array of objects ({text|label|point|value|claim}), or a single
   delimited string. Returns [] when nothing usable is present. */
function parseProofPoints(raw: unknown): string[] {
  const out: string[] = [];
  const pushStr = (s: unknown) => {
    if (typeof s === "string" && s.trim()) out.push(s.trim());
  };
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === "string") pushStr(item);
      else if (item && typeof item === "object") {
        const o = item as Record<string, unknown>;
        pushStr(o.text ?? o.label ?? o.point ?? o.value ?? o.claim ?? o.title);
      }
    }
  } else if (typeof raw === "string") {
    raw.split(/[\n·•|]+/).forEach(pushStr);
  }
  return out;
}

/* ─────────────────────────────────────────────
   REVIEWS DATA
───────────────────────────────────────────── */

const LIP_BALM_REVIEWS = [
  { name: "Victoria", scent: "Peppermint",   quote: "Best lip balm I have ever used. After one week **the flaky dry skin is gone**. I tell everyone **how amazing it is**." },
  { name: "Pamela",   scent: null,           quote: "I found Papabear and **I will never go back**. I love how **my lips feel all through the day**. They don't feel dried out all the time." },
  { name: "Olivia",   scent: "Raw Honey",    quote: "I've tried nearly every lip balm there is and **nothing works quite like this one**. **1000000/10 recommend** to anyone seeking a natural swap!" },
  { name: "Ronna",    scent: "Mixed Scents", quote: "**Lips are healing and staying healed**. I'm not experiencing **any sensitivity issues**. So glad I took the chance on this product." },
  { name: "Karli",    scent: "Mixed Scents", quote: "I struggle with dry chapped lips year round and **my daughter and I both love this**! **Will be repurchasing** and gifting one to my sister too!" },
];

const EYE_CREAM_REVIEWS = [
  { name: "Sharon",  scent: null, quote: "**Best eye cream ever!** Using it at night has **transformed the skin around my eyes**. Superb product!" },
  { name: "Gage",    scent: null, quote: "**Great for tightening bags** and keeping skin firm. **Removes bags with ease!** Feels great overall." },
  { name: "Lynette", scent: null, quote: "**Helps with my morning puffy eyes.** Simple, fast, and it actually works." },
  { name: "Lacey",   scent: null, quote: "Love this stuff! **It has changed my skin for the better!**" },
  { name: "Theresa", scent: null, quote: "**Love the eye cream** and all their products! Everything from Papabear has been incredible." },
];

const BALM_REVIEWS = [
  { name: "Katrina",  scent: "Vanilla",    quote: "I use it on my grandson for his eczema. **Helps the redness go away** and **really makes his skin soft**. Does not stay greasy. Absorbs very well." },
  { name: "Jennifer", scent: "Vanilla",    quote: "I just **ordered my second jar** for my husband, who **kept dipping into my jar**. Says it all!" },
  { name: "Ginny",    scent: "Unscented",  quote: "**My face feels so good** and it will **last such a long time**! I have very dry skin and this works super great for me. Frankly I'm thrilled." },
  { name: "Samuel",   scent: "Vanilla",    quote: "**Goes on so smooth** and **a little bit goes a long way**! Container is bigger than expected. **Definitely worth the price**." },
  { name: "Deven",    scent: "Lavender",   quote: "**Hands down the best moisturizer I've ever tried**. It's **helped my skin issues tremendously**!!" },
];

function renderBold(text: string) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i} style={{ fontWeight: 700, color: "var(--color-brand-deepnavy)" }}>{part}</strong> : part
  );
}

/* ─────────────────────────────────────────────
   AI SECTION IMAGE SLOT
   Mirrors FarmstandSection's OriginMedia: durable R2 imageUrl → <img>;
   else a spinner while the slot is still rendering; else the dashed
   MediaPlaceholder fallback. Keeps the long-form page fully silent-fallback.
───────────────────────────────────────────── */

type LpImgSlot = {
  key?: string;
  imageUrl?: string;
  altText?: string;
  imageStatus?: string;
  imageLoading?: boolean;
};

function SlotImageLoader({ style, rounded = false }: { style?: React.CSSProperties; rounded?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: 80,
        backgroundColor: "#E8E2D6",
        border: "2px dashed #B8AFA0",
        borderRadius: rounded ? 10 : 0,
        boxSizing: "border-box" as const,
        ...style,
      }}
    >
      <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#6B6560" }} />
    </div>
  );
}

// Retry affordance box shown when a section-image slot failed AND a retry
// handler is available (in-app builder only). Clicking regenerates just this
// one slot.
function SlotImageRetry({
  slotKey,
  onRetry,
  style,
  rounded = false,
}: {
  slotKey: string;
  onRetry: (slotKey: string) => void;
  style?: React.CSSProperties;
  rounded?: boolean;
}) {
  const [clicked, setClicked] = useState(false);
  return (
    <div
      data-testid={`retry-lp-section-${slotKey}`}
      style={{
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        width: "100%",
        minHeight: 80,
        backgroundColor: "#EFE9DC",
        border: "2px dashed #B8AFA0",
        borderRadius: rounded ? 10 : 0,
        boxSizing: "border-box" as const,
        padding: 12,
        ...style,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 700, color: "#8A7F68", textAlign: "center" as const }}>
        Image failed to generate
      </span>
      <button
        type="button"
        disabled={clicked}
        onClick={() => {
          setClicked(true);
          onRetry(slotKey);
        }}
        data-testid={`button-retry-lp-section-${slotKey}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          borderRadius: 999,
          border: "1px solid var(--color-brand-navyaccent)",
          backgroundColor: "#FFFFFF",
          color: "var(--color-brand-navyaccent)",
          fontSize: 12,
          fontWeight: 700,
          cursor: clicked ? "default" : "pointer",
          opacity: clicked ? 0.6 : 1,
        }}
      >
        <RotateCw style={{ width: 13, height: 13 }} className={clicked ? "animate-spin" : ""} />
        {clicked ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}

function SlotImage({
  slot,
  fallbackPath,
  alt,
  rounded = false,
  style,
  loading = false,
  onRetry,
  overrideKey,
  regenKey,
}: {
  slot?: LpImgSlot;
  fallbackPath: string;
  alt?: string;
  rounded?: boolean;
  style?: React.CSSProperties;
  loading?: boolean;
  onRetry?: (slotKey: string) => void;
  /** Stable media-override key (e.g. "proofImages:material-texture"); also the
   *  key the upload/revert controls read & write. */
  overrideKey?: string;
  /** AI slot key used to regenerate — only AI slots support regeneration. */
  regenKey?: string;
}) {
  const showStatusDots = useLpStatusDots();
  const overrideUrl = useLpMediaOverride(overrideKey);
  const displaySrc = overrideUrl || slot?.imageUrl;
  const canRegen = !!regenKey && !!onRetry && !!slot?.imageUrl;
  const actions = overrideKey ? (
    <LpImageActions
      slotKey={overrideKey}
      canRegenerate={canRegen}
      onRegenerate={regenKey && onRetry ? () => onRetry(regenKey) : undefined}
      testIdPrefix={overrideKey.replace(/[^a-zA-Z0-9_-]/g, "-")}
    />
  ) : null;
  let body: React.ReactNode;
  if (displaySrc) {
    body = (
      <>
        <img
          src={displaySrc}
          alt={slot?.altText || alt || ""}
          data-testid={`img-lp-section-${slot?.key || fallbackPath}`}
          style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
        />
        {showStatusDots && !overrideUrl && (
          <span
            title="AI-generated image"
            aria-label="AI-generated image"
            data-testid={`dot-ai-image-${slot?.key || "img"}`}
            style={{ position: "absolute", top: 8, right: 8, width: 10, height: 10, borderRadius: "50%", backgroundColor: "#22C55E", boxShadow: "0 0 0 3px rgba(255,255,255,0.7), 0 1px 3px rgba(0,0,0,0.3)", zIndex: 2 }}
          />
        )}
      </>
    );
  } else if (loading || slot?.imageLoading) {
    body = <SlotImageLoader rounded={rounded} style={style} />;
  } else if (onRetry && slot?.key && slot?.imageStatus === "failed") {
    body = <SlotImageRetry slotKey={slot.key} onRetry={onRetry} rounded={rounded} style={style} />;
  } else {
    body = <MediaPlaceholder path={fallbackPath} rounded={rounded} style={style} />;
  }
  return (
    <div className="relative group/lpimg" style={{ width: "100%", borderRadius: rounded ? 10 : 0, overflow: "hidden", ...style }}>
      {body}
      {actions}
    </div>
  );
}

// Pick a slot by key from an AI image array; mark it loading when the slot's own
// generation is in flight, or (before the slot exists yet) while the whole
// section-image pipeline is loading. A slot that already exists but FAILED is
// not forced to spin — it keeps its "failed" status so a single-slot retry
// doesn't make sibling failed slots appear to regenerate too.
function resolveImgSlot(
  images: LpImgSlot[] | undefined,
  key: string,
  pipelineLoading: boolean,
): LpImgSlot | undefined {
  const s = images?.find((x) => x?.key === key);
  if (s?.imageUrl) return s;
  if (s?.imageLoading || s?.imageStatus === "pending") return { ...s, key, imageLoading: true };
  if (pipelineLoading && !s) return { key, imageLoading: true };
  return s;
}

// Fallback chain helper: first non-empty trimmed string wins; else "".
function pick(...vals: Array<string | undefined | null>): string {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

// Status dot marking the source of a piece of content:
//   workflow    → green  (generated live by a Sim workflow / FAL image)
//   database    → yellow (pulled from the database)
//   placeholder → red    (hardcoded static fallback content)
type ContentSource = "workflow" | "database" | "placeholder";
const SOURCE_META: Record<ContentSource, { color: string; title: string }> = {
  workflow: { color: "#22C55E", title: "Generated by an AI workflow" },
  database: { color: "#EAB308", title: "Pulled from the database" },
  placeholder: { color: "#EF4444", title: "Static placeholder content" },
};
function SourceDot({ source, className = "" }: { source: ContentSource; className?: string }) {
  if (!useLpStatusDots()) return null;
  const meta = SOURCE_META[source];
  // The green (workflow) variant renders out-of-flow, pinned to the top-right of
  // its relatively-positioned parent, so the "AI-generated" indicator never
  // claims its own line or shifts the eyebrow/headline it follows. The yellow
  // (database) / red (placeholder) variants stay inline as before.
  if (source === "workflow") {
    return (
      <span
        title={meta.title}
        aria-label={meta.title}
        data-testid={`dot-source-${source}`}
        className={className}
        style={{
          position: "absolute",
          top: -2,
          right: 0,
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: meta.color,
          boxShadow: `0 0 0 3px ${meta.color}2e`,
          pointerEvents: "none",
        }}
      />
    );
  }
  return (
    <span
      title={meta.title}
      aria-label={meta.title}
      data-testid={`dot-source-${source}`}
      className={className}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: meta.color,
        boxShadow: `0 0 0 3px ${meta.color}2e`,
        flexShrink: 0,
        verticalAlign: "middle",
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   AI COPY TYPES — mirror the backend extractors in
   server/services/lp-express-worker.ts. Every field is optional and falls
   back to the template's hardcoded copy when absent/empty.
───────────────────────────────────────────── */

type TimelineCopy = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  steps?: Array<{ label?: string; headline?: string; bullets?: string[]; hidden?: boolean }>;
  disclaimer?: string;
  sticky_note_headline?: string;
};
type MembershipCopy = {
  section_eyebrow?: string;
  section_headline?: string;
  body_1?: string;
  body_2?: string;
  body_closing?: string;
  membership_label?: string;
  membership_tagline?: string;
  benefits?: Array<{ icon_hint?: string; label?: string; note?: string }>;
  objection_note_1?: string;
  objection_note_2?: string;
  objection_note_3?: string;
  // Member card display fields. Each falls back to its sample literal when the
  // membership workflow does not supply a value.
  location_label?: string;
  member_name?: string;
  member_since?: string;
  member_next_ship?: string;
  member_id?: string;
};
type AlternativesCopy = {
  section_eyebrow?: string;
  hero_headline?: string;
  hero_subheadline?: string;
  hero_differentiators?: string[];
  competitor_intro?: string;
  competitor_cards?: Array<{ title?: string; body?: string; hidden?: boolean }>;
  comparison_them_label?: string;
  comparison_us_label?: string;
  comparison_them?: string[];
  comparison_us?: string[];
  closing_line_1?: string;
  closing_line_2?: string;
};
type PhilosophyCopy = {
  section_eyebrow?: string;
  section_headline?: string;
  section_body_1?: string;
  section_body_2?: string;
  pillar_card_label?: string;
  pillars?: Array<{ num?: string; title?: string; body?: string }>;
  pillar_footer_words?: string[];
};
type DifferentiatorCopy = {
  section_eyebrow?: string;
  section_headline?: string;
  features?: Array<{ icon_hint?: string; title?: string; body?: string }>;
  trust_badge?: string;
};
type FaqCopy = { faqs?: Array<{ question?: string; answer?: string }> };
type ReviewsCopy = {
  reviews?: Array<{ name?: string; variant?: string | null; quote?: string; verified?: boolean; hidden?: boolean }>;
};
type FinalCtaCopy = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  bundle_label?: string;
  savings_badge?: string;
  cta_button?: string;
  cta_subtext?: string;
  trust_badges?: Array<{ icon_hint?: string; label?: string }>;
  social_proof_line?: string;
};
type FooterCopy = {
  tagline?: string;
  description?: string;
  explore_label?: string;
  support_label?: string;
  explore_links?: Array<{ label?: string; url?: string }>;
  support_links?: Array<{ label?: string; url?: string }>;
  social_links?: Array<{ url?: string }>;
  copyright?: string;
  bottom_note?: string;
};

/* ─────────────────────────────────────────────
   REUSABLE: Farmstand receipt buy box
  (used in S11 and Final CTA — slight variation)
───────────────────────────────────────────── */

export function FarmstandReceiptBox({ receipt, brandName, checkoutUrl }: { receipt?: LpReceipt; brandName?: string | null; checkoutUrl?: string | null }) {
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  const v2 = useLpV2();
  // When a checkoutUrl is explicitly supplied (even ""), use it verbatim — an
  // empty string yields an inert button (no href). Call sites that omit the
  // prop keep the template's hardcoded CHECKOUT_URL.
  const href = checkoutUrl !== undefined ? (checkoutUrl || undefined) : CHECKOUT_URL;
  return (
    <div style={{ background: "#FFFCF5", border: "2px dashed #D8CFC3", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ backgroundColor: "var(--color-brand-deepnavy)", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "var(--color-brand-cream)", fontWeight: 900, fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase" as const }}>{v2 ? "Your Order" : "Farmstand Order"}</span>
        <span style={{ color: "rgba(244,237,221,0.5)", fontSize: 10, fontWeight: 600 }}>{brandName?.trim() || (v2 ? "Your Brand" : "Papabear Naturals")}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "0 4px", marginTop: -1 }}>
        {Array.from({ length: 20 }).map((_, i) => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "var(--color-brand-cream)", marginTop: -4 }} />)}
      </div>
      <div style={{ padding: "8px 16px 0" }}>
        {r.lineItems.map((item, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px dashed #D8CFC3" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-brand-deepnavy)" }}>{item.name}</span>
            {item.priceLabel && <span style={{ fontSize: 13, fontWeight: 700, color: "#6B6560", textDecoration: r.hasDiscount ? "line-through" : "none" }}>{item.priceLabel}</span>}
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-brand-deepnavy)" }}>U.S. Shipping</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-brand-greenaccent)" }}>FREE</span>
        </div>
      </div>
      {r.hasDiscount && (
        <div style={{ padding: "6px 16px", borderTop: "2px solid var(--color-brand-deepnavy)", marginTop: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: "var(--color-brand-deepnavy)", letterSpacing: "0.06em", textTransform: "uppercase" as const }}>Retail Total</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#6B6560", textDecoration: "line-through" }}>{r.retailLabel}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: "var(--color-brand-greenaccent)", letterSpacing: "0.06em", textTransform: "uppercase" as const }}>{r.discountRowLabel}{r.discountPercent ? ` (${r.discountPercent}% off)` : ""}</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: "var(--color-brand-greenaccent)" }}>{r.discountAmountLabel}</span>
          </div>
        </div>
      )}
      <div style={{ padding: "10px 16px", borderTop: "2px dashed #D8CFC3", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 900, color: "var(--color-brand-deepnavy)", letterSpacing: "0.1em", textTransform: "uppercase" as const }}>You Pay Today</span>
        <span style={{ fontSize: 28, fontWeight: 900, color: "var(--color-brand-navyaccent)" }}>{r.priceTodayLabel}</span>
      </div>
      <div style={{ padding: "0 16px 14px" }}>
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: "block", width: "100%", padding: "14px 0", backgroundColor: "var(--color-brand-greenaccent)", color: "#FFFFFF", fontSize: 15, fontWeight: 800, textAlign: "center" as const, borderRadius: 10, textDecoration: "none", boxSizing: "border-box" as const, letterSpacing: "0.04em" }}>
          {r.ctaText}
        </a>
        <div style={{ display: "flex", flexWrap: "wrap" as const, gap: "3px 12px", justifyContent: "center", marginTop: 8 }}>
          {r.guaranteeBullets.map(t => (
            <div key={t} style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <CheckCircle2 style={{ width: 10, height: 10, color: "var(--color-brand-greenaccent)", flexShrink: 0 }} />
              <span style={{ fontSize: 9, fontWeight: 600, color: "#5A554F" }}>{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SPRING SALE BANNER
───────────────────────────────────────────── */

function SpringSaleBanner({ receipt }: { receipt?: LpReceipt }) {
  // Pull the marquee straight from the offer/sale: only render when there is a
  // real discount, and show the actual sale label + percent off.
  if (!receipt?.hasDiscount || !receipt?.discountPercent) return null;
  const label = receipt.discountRowLabel?.trim() || "Sale";
  const percent = receipt.discountPercent;
  return (
    <div style={{ backgroundColor: "var(--color-brand-deepnavy)", padding: "12px 0", overflow: "hidden", display: "flex", whiteSpace: "nowrap" as const }}>
      {[0, 1].map(g => (
        <div key={g} className="flex items-center gap-6 animate-[marqueeLp_20s_linear_infinite]" style={{ paddingLeft: g === 1 ? 24 : 0 }}>
          {Array(8).fill(0).map((_, i) => (
            <span key={i} style={{ display: "contents" }}>
              <Star className="fill-current w-5 h-5 shrink-0" style={{ color: "var(--color-brand-goldaccent)" }} />
              <span style={{ fontSize: 13, fontWeight: 900, color: "var(--color-brand-cream)", letterSpacing: "0.08em", textTransform: "uppercase" as const, flexShrink: 0 }}>{label}</span>
              <span style={{ fontSize: 13, color: "rgba(244,237,221,0.4)", flexShrink: 0 }}>·</span>
              <span style={{ fontSize: 13, fontWeight: 900, color: "var(--color-brand-coral)", letterSpacing: "0.08em", flexShrink: 0 }}>{percent}% OFF</span>
            </span>
          ))}
        </div>
      ))}
      <style>{`@keyframes marqueeLp { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────
   S5 – 90-DAY SKIN TIMELINE
───────────────────────────────────────────── */

const TIMELINE_STEPS = [
  { time: "WEEK 1",  tabColor: "var(--color-brand-navyaccent)", headline: "Your face starts feeling more comfortable",  points: ["Lips feel smoother and better hydrated", "Under-eyes look less dry and tired", "Skin feels nourished, no greasy finish"] },
  { time: "30 DAYS", tabColor: "#2D5A8B", headline: "Dryness starts calming down",                points: ["Rough patches begin to soften", "Lips stay smoother for longer", "Skin around eyes looks more refreshed"] },
  { time: "60 DAYS", tabColor: "#2D6A6B", headline: "Healthy-looking skin shows up",               points: ["Face looks more even and nourished", "Fine lines from dryness less noticeable", "Skin feels softer, calmer, more resilient"] },
  { time: "90 DAYS", tabColor: "var(--color-brand-greenaccent)", headline: "This is your healthier baseline",             points: ["Face looks consistently more cared for", "Lips, under-eyes, and skin all supported", "Simple routine delivering visible payoff"] },
];

// Brand-neutral timeline fallback for Long Form v2 — category-agnostic copy used
// before AI-generated steps populate. v1 never references this.
const TIMELINE_STEPS_V2 = [
  { time: "WEEK 1",  tabColor: "var(--color-brand-navyaccent)", headline: "You start noticing the difference",  points: ["First impressions feel great", "The quality is immediately obvious", "It fits easily into your routine"] },
  { time: "30 DAYS", tabColor: "#2D5A8B", headline: "It becomes part of your routine",    points: ["Consistent, reliable results", "You reach for it without thinking", "The value starts to add up"] },
  { time: "60 DAYS", tabColor: "#2D6A6B", headline: "The results really show",             points: ["Noticeable, lasting improvement", "People start to take notice", "You're glad you made the switch"] },
  { time: "90 DAYS", tabColor: "var(--color-brand-greenaccent)", headline: "This is your new baseline",           points: ["Dependable results every time", "A simple routine that delivers", "Exactly what you were looking for"] },
];

const LH = 30;
const ML = 44;
const PL = ML + 8;
const HOLE_W = 20;

function TimelineSection({ timeline, loading = false, receipt, checkoutUrl }: { timeline?: TimelineCopy; loading?: boolean; receipt?: LpReceipt; checkoutUrl?: string | null }) {
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  const v2 = useLpV2();
  const TAB_COLORS = v2
    ? ["var(--color-brand-navyaccent)", "var(--color-brand-coral)", "var(--color-brand-gold)", "var(--color-brand-greenaccent)"]
    : ["var(--color-brand-navyaccent)", "#2D5A8B", "#2D6A6B", "var(--color-brand-greenaccent)"];
  const aiStepEntries = (timeline?.steps ?? [])
    .map((s, idx) => ({ s, idx }))
    .filter(({ s }) => pick(s?.headline) || pick(s?.label) || (s?.bullets && s.bullets.length));
  const aiSteps = aiStepEntries.map((e) => e.s);
  const steps = aiStepEntries.length
    ? aiStepEntries.map(({ s, idx }, i) => ({
        time: pick(s?.label) || TIMELINE_STEPS[i % TIMELINE_STEPS.length].time,
        timeFallback: TIMELINE_STEPS[i % TIMELINE_STEPS.length].time,
        rawLabel: pick(s?.label),
        tabColor: TAB_COLORS[i % TAB_COLORS.length],
        headline: pick(s?.headline),
        points: (s?.bullets ?? [])
          .map((b, j) => ({ text: b, bulletIndex: j }))
          .filter((p) => typeof p.text === "string" && p.text.trim()),
        storedIndex: idx,
        hidden: s?.hidden,
      }))
    : (v2 ? TIMELINE_STEPS_V2 : TIMELINE_STEPS).map((s) => ({
        time: s.time,
        timeFallback: s.time,
        rawLabel: "",
        tabColor: s.tabColor,
        headline: s.headline,
        points: s.points.map((pt) => ({ text: pt, bulletIndex: -1 })),
        storedIndex: -1,
        hidden: undefined as boolean | undefined,
      }));
  const eyebrow = pick(timeline?.section_eyebrow) || (v2 ? "Your Journey" : "Your Skin Journey");
  const aiHeadline = pick(timeline?.section_headline);
  const subheadline = pick(timeline?.section_subheadline) || "Here's what consistent use may look like.";
  const disclaimer = pick(timeline?.disclaimer) || (v2 ? "*Results may vary." : "*Results may vary. Not evaluated by the FDA.");
  const stickyHeadline = pick(timeline?.sticky_note_headline) || "What you get";
  const showHeadlineSpinner = !aiHeadline && loading;
  const isAi = !!(aiHeadline || (aiSteps && aiSteps.length) || pick(timeline?.section_eyebrow) || pick(timeline?.section_subheadline) || pick(timeline?.disclaimer) || pick(timeline?.sticky_note_headline));
  return (
    <section style={{ backgroundColor: "var(--color-brand-cream)", padding: "40px 16px 28px", borderTop: "1px solid #D8CFC3", borderBottom: "1px solid #D8CFC3" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <LpReorderGroup
          groupKey="timeline:blocks"
          items={[
            { key: "timeline:notebook-card", node: (
        <EditableElement elementKey="timeline:notebook-card" testId="element-timeline-notebook">
        <div style={{ backgroundColor: "#FFFEF9", borderRadius: 6, boxShadow: "0 2px 16px rgba(0,0,0,0.08)", overflow: "hidden", position: "relative" as const }}>
          {/* Spiral strip */}
          <div style={{ position: "absolute" as const, left: 0, top: 0, bottom: 0, width: HOLE_W, zIndex: 3, backgroundColor: "#F0E9DA", borderRight: "1px solid #DDD6C8" }}>
            {Array(30).fill(0).map((_, i) => (
              <div key={i} style={{ position: "absolute" as const, top: LH / 2 - 4 + i * LH, left: 5, width: 9, height: 9, borderRadius: "50%", border: "1.5px solid #B8AFA3", backgroundColor: "var(--color-brand-cream)" }} />
            ))}
          </div>
          {/* Red margin line */}
          <div style={{ position: "absolute" as const, left: ML, top: 0, bottom: 0, width: 1, backgroundColor: "rgba(200,80,80,0.25)", zIndex: 1 }} />
          {/* Ruled lines */}
          <div style={{ position: "absolute" as const, inset: 0, backgroundImage: `repeating-linear-gradient(transparent, transparent ${LH - 1}px, rgba(180,170,155,0.18) ${LH - 1}px, rgba(180,170,155,0.18) ${LH}px)`, zIndex: 0, pointerEvents: "none" as const }} />

          {/* Header */}
          <div style={{ paddingLeft: PL, paddingRight: 20, position: "relative" as const, zIndex: 1 }}>
            <div style={{ position: "relative" as const, height: LH, display: "flex", alignItems: "center", gap: 6 }}><EditableText as="span" elementKey="timeline:eyebrow" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.2em", color: "#A0896C", textTransform: "uppercase" as const }} formatOnly path={["timeline", "section_eyebrow"]} value={pick(timeline?.section_eyebrow)} placeholder={v2 ? "Your Journey" : "Your Skin Journey"} /><SourceDot source={isAi ? "workflow" : "placeholder"} /></div>
            {aiHeadline ? (
              <div style={{ minHeight: LH * 2, display: "flex", alignItems: "center" }} data-testid="text-timeline-headline"><EditableText as="h3" elementKey="timeline:headline" className="font-black" style={{ fontSize: 20, color: "var(--color-brand-navyaccent)", margin: 0, lineHeight: 1.15 }} formatOnly path={["timeline", "section_headline"]} value={aiHeadline} placeholder="" multiline /></div>
            ) : showHeadlineSpinner ? (
              <div style={{ minHeight: LH * 2, display: "flex", alignItems: "center" }} data-testid="loading-timeline-headline"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--color-brand-navyaccent)", opacity: 0.5 }} /></div>
            ) : (
              <>
                <div style={{ height: LH, display: "flex", alignItems: "center" }}><EditableText as="h3" elementKey="timeline:headline" className="font-black" style={{ fontSize: 20, color: "var(--color-brand-navyaccent)", margin: 0, lineHeight: 1 }} formatOnly path={["timeline", "headline_fallback_1"]} value={(timeline as any)?.headline_fallback_1} placeholder={v2 ? "What You May" : "What Your Face May"} /></div>
                <div style={{ height: LH, display: "flex", alignItems: "center" }}><EditableText as="h3" elementKey="timeline:headline" className="font-black" style={{ fontSize: 20, color: "var(--color-brand-navyaccent)", margin: 0, lineHeight: 1 }} formatOnly path={["timeline", "headline_fallback_2"]} value={(timeline as any)?.headline_fallback_2} placeholder={v2 ? "Notice Over Time" : "Notice in 90 Days"} /></div>
              </>
            )}
            <div style={{ height: LH, display: "flex", alignItems: "center" }}><EditableText as="p" elementKey="timeline:subheadline" style={{ fontSize: 12, color: "#8A8279", margin: 0, fontStyle: "italic" as const }} formatOnly path={["timeline", "section_subheadline"]} value={pick(timeline?.section_subheadline)} placeholder="Here's what consistent use may look like." multiline /></div>
          </div>

          <div style={{ height: 1, background: "#E0D8CA", marginLeft: PL, marginRight: 20, position: "relative" as const, zIndex: 1 }} />

          {steps.map((step, i) => {
            const editable = step.storedIndex >= 0;
            const stepIndex = step.storedIndex >= 0 ? step.storedIndex : i;
            const stepBody = (
              <>
                <div style={{ height: LH, display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.12em", color: "#FFF", backgroundColor: step.tabColor, padding: "3px 10px", borderRadius: 3 }}>
                    <EditableText as="span" path={["timeline", "steps", stepIndex, "label"]} value={step.rawLabel} placeholder={step.timeFallback} />
                  </span>
                </div>
                <div style={{ minHeight: LH, display: "flex", alignItems: "center", paddingTop: 4, paddingBottom: 4 }}>
                  <h4 className="font-bold" style={{ fontSize: 14, color: "var(--color-brand-deepnavy)", margin: 0, lineHeight: 1.2 }}>
                    <EditableText as="span" path={["timeline", "steps", stepIndex, "headline"]} value={step.headline} placeholder="" />
                  </h4>
                </div>
                {step.points.map((pt, j) => (
                  <div key={j} style={{ minHeight: LH, display: "flex", alignItems: "center", gap: 7, paddingTop: 2, paddingBottom: 2 }}>
                    <span style={{ color: step.tabColor, fontSize: 6, flexShrink: 0 }}>&#9632;</span>
                    <span style={{ fontSize: 13, color: "#5A554F" }}>
                      <EditableText as="span" path={["timeline", "steps", stepIndex, "bullets", pt.bulletIndex >= 0 ? pt.bulletIndex : j]} value={pt.text} placeholder="" multiline />
                    </span>
                  </div>
                ))}
              </>
            );
            return (
              <motion.div key={i} style={{ paddingLeft: PL, paddingRight: 20, position: "relative" as const, zIndex: 1 }} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-10px" }} transition={{ duration: 0.3, delay: i * 0.08 }}>
                {editable ? (
                  <EditableItem path={["timeline", "steps"]} index={step.storedIndex} count={timeline?.steps?.length ?? 0} hidden={step.hidden} allowDelete testId={`timeline-step-${step.storedIndex}`}>
                    {stepBody}
                  </EditableItem>
                ) : stepBody}
                {i < steps.length - 1 && <div style={{ height: LH / 2 }} />}
              </motion.div>
            );
          })}

          <div style={{ paddingLeft: PL, paddingRight: 20, position: "relative" as const, zIndex: 1 }}>
            <div style={{ height: LH, display: "flex", alignItems: "center" }}>
              <EditableText as="p" style={{ fontSize: 9, color: "#B0A89E", margin: 0, fontStyle: "italic" as const }} formatOnly path={["timeline", "disclaimer"]} value={pick(timeline?.disclaimer)} placeholder={v2 ? "*Results may vary." : "*Results may vary. Not evaluated by the FDA."} multiline />
            </div>
          </div>
        </div>
        </EditableElement>
            ) },
            { key: "timeline:buy-box", node: (
        /* Sticky note buy box — whole card is one hide/delete unit */
        <EditableElement elementKey="timeline:buy-box" testId="element-timeline-buy-box">
        <div style={{ maxWidth: 340, margin: "24px auto 0", position: "relative" as const }}>
          <div style={{ position: "absolute" as const, top: -10, left: "50%", transform: "translateX(-50%) rotate(-1deg)", width: 80, height: 22, background: "rgba(200,190,170,0.45)", borderRadius: 2, zIndex: 4 }} />
          <div style={{ backgroundColor: "#FFF9C4", borderRadius: 3, padding: "28px 24px 22px", boxShadow: "2px 3px 12px rgba(0,0,0,0.10)", transform: "rotate(-0.8deg)", position: "relative" as const }}>
            <EditableText as="p" style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: "#A08850", textTransform: "uppercase" as const, marginBottom: 14 }} formatOnly path={["timeline", "sticky_note_headline"]} value={pick(timeline?.sticky_note_headline)} placeholder="What you get" />
            {r.lineItems.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: "var(--color-brand-greenaccent)" }}>{String(i + 1).padStart(2, "0")}</span>
                  <span style={{ fontSize: 13, color: "#3A3520" }}>{item.name}</span>
                </div>
                {item.priceLabel && <span style={{ fontSize: 11, color: "#A09880", textDecoration: r.hasDiscount ? "line-through" : "none" }}>{item.priceLabel}</span>}
              </div>
            ))}
            <div style={{ borderTop: "2px dashed #D4C88A", margin: "18px 0" }} />
            {r.hasDiscount && (
              <>
                <div style={{ textAlign: "center" as const, marginBottom: 6 }}>
                  <EditableText as="span" style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", color: "#B0A080", textTransform: "uppercase" as const }} formatOnly path={["timeline", "retail_value_label"]} value={(timeline as any)?.retail_value_label} placeholder="Retail value" />
                </div>
                <div style={{ position: "relative" as const, display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%", marginBottom: 8 }}>
                  <span style={{ fontSize: 28, fontWeight: 800, color: "#8A7E6A" }}>{r.retailLabel}</span>
                  <svg width="100%" height="16" viewBox="0 0 160 16" preserveAspectRatio="none" style={{ position: "absolute" as const, left: "15%", top: "50%", transform: "translateY(-50%) rotate(-2deg)", width: "70%", opacity: 0.85 }}>
                    <path d="M2 10 Q40 4 80 9 Q120 14 158 6" fill="none" stroke="#D42B2B" strokeWidth="3.5" strokeLinecap="round" />
                  </svg>
                </div>
              </>
            )}
            <div style={{ textAlign: "center" as const, marginBottom: 4 }}>
              <EditableText as="span" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: "var(--color-brand-greenaccent)", textTransform: "uppercase" as const }} formatOnly path={["timeline", "price_today_label"]} value={(timeline as any)?.price_today_label} placeholder="Your price today" />
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 8 }}>
              <span style={{ fontSize: 52, fontWeight: 900, color: "var(--color-brand-navyaccent)", lineHeight: 1 }}>{r.priceTodayLabel}</span>
              {r.hasDiscount && r.discountPercent && (
                <div style={{ backgroundColor: "var(--color-brand-coral)", color: "#FFFFFF", borderRadius: "50%", width: 68, height: 68, display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "center", transform: "rotate(6deg)", boxShadow: "0 3px 12px rgba(242,105,103,0.4)", flexShrink: 0 }}>
                  <span style={{ fontSize: 22, fontWeight: 900, lineHeight: 1 }}>{r.discountPercent}%</span>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em" }}>OFF</span>
                </div>
              )}
            </div>
            {r.savingsLabel && (
              <div style={{ backgroundColor: v2 ? "color-mix(in srgb, var(--color-brand-greenaccent) 8%, transparent)" : "rgba(45,122,79,0.08)", border: "1px dashed var(--color-brand-greenaccent)", borderRadius: 6, padding: "6px 12px", textAlign: "center" as const, marginBottom: 18 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: "var(--color-brand-greenaccent)" }}>{r.savingsLabel}</span>
              </div>
            )}
            <a href={v2 ? (checkoutUrl || undefined) : CHECKOUT_URL} target="_blank" rel="noopener noreferrer" style={{ display: "block", background: v2 ? "var(--color-brand-greenaccent)" : "linear-gradient(135deg, var(--color-brand-greenaccent) 0%, #236B42 100%)", borderRadius: 8, padding: "14px 16px", textAlign: "center" as const, textDecoration: "none", boxShadow: v2 ? "0 3px 10px rgba(0,0,0,0.22)" : "0 3px 10px rgba(45,122,79,0.3)", marginTop: r.savingsLabel ? 0 : 18 }}>
              <p style={{ color: "#fff", fontWeight: 900, fontSize: 14, margin: 0 }}>{r.ctaText}</p>
              <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 10, margin: 0, marginTop: 3, fontWeight: 500 }}>Free shipping · {r.guaranteeDays}-Day guarantee</p>
            </a>
          </div>
        </div>
        </EditableElement>
            ) },
          ]}
        />
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   S10 – BUILT FOR FRESHNESS (Ledger)
───────────────────────────────────────────── */

function FreshnessSection({
  valueReframe,
  loading = false,
  membership,
  membershipLoading = false,
  receipt,
  logoUrl,
  brandName,
}: {
  valueReframe?: string;
  loading?: boolean;
  membership?: MembershipCopy;
  membershipLoading?: boolean;
  receipt?: LpReceipt;
  logoUrl?: string | null;
  brandName?: string | null;
}) {
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  const HARDCODED_BENEFITS = [
    r.hasDiscount
      ? { icon: r.discountPercent ? `${r.discountPercent}%` : "SALE", label: `First order ${r.priceTodayLabel}`, note: r.retailLabel ? `retail ${r.retailLabel}` : "" }
      : { icon: "★", label: `Just ${r.priceTodayLabel}`, note: "small-batch quality" },
    { icon: "★",   label: "Made fresh to order",      note: "never warehoused" },
    { icon: `${r.guaranteeDays}d`, label: `Auto-ships every ${r.guaranteeDays} days`, note: "skip or cancel anytime" },
    { icon: r.refillLabel ?? "$", label: "Locked-in refills",         note: "member price guaranteed" },
    { icon: "✓",   label: "Free shipping",             note: `${r.guaranteeDays}-day guarantee` },
  ];
  const aiBenefitEntries = (membership?.benefits ?? [])
    .map((b, idx) => ({ b, idx }))
    .filter(({ b }) => pick(b?.label));
  const aiBenefits = aiBenefitEntries.map((e) => e.b);
  const MEMBER_BENEFITS = aiBenefitEntries.length
    ? aiBenefitEntries.map(({ b, idx }, i) => ({
        icon: pick(b?.icon_hint) || HARDCODED_BENEFITS[i % HARDCODED_BENEFITS.length].icon,
        label: pick(b?.label),
        labelRaw: pick(b?.label),
        note: pick(b?.note),
        noteRaw: pick(b?.note),
        storedIndex: idx,
      }))
    : HARDCODED_BENEFITS.map((b) => ({ ...b, labelRaw: "", noteRaw: "", storedIndex: -1 }));

  // Precedence: AI membership headline → value_reframe pull-quote → hardcoded.
  const aiHeadline = pick(membership?.section_headline);
  const headlineSpinner = !aiHeadline && !pick(valueReframe) && (loading || membershipLoading);
  const eyebrow = pick(membership?.section_eyebrow) || "How the Membership Works";
  const v2 = useLpV2();
  const brandDisplayName = pick(brandName) || (v2 ? "Your Brand" : "Papabear Naturals");
  const memberFields = [
    { labelKey: "member_name_label", labelPlaceholder: "Member", valKey: "member_name", valRaw: pick(membership?.member_name), valPlaceholder: "Sarah M.", color: "var(--color-brand-deepnavy)", mono: false },
    { labelKey: "member_since_label", labelPlaceholder: "Since", valKey: "member_since", valRaw: pick(membership?.member_since), valPlaceholder: "Jan 2024", color: "var(--color-brand-deepnavy)", mono: false },
    { labelKey: "member_next_ship_label", labelPlaceholder: "Next Ship", valKey: "member_next_ship", valRaw: pick(membership?.member_next_ship), valPlaceholder: "May 12", color: "var(--color-brand-greenaccent)", mono: false },
    { labelKey: "member_id_label", labelPlaceholder: "ID", valKey: "member_id", valRaw: pick(membership?.member_id), valPlaceholder: v2 ? "M-1024" : "PB-4821", color: "var(--color-brand-deepnavy)", mono: true },
  ];
  const body1 = pick(membership?.body_1);
  const body2 = pick(membership?.body_2);
  const bodyClosing = pick(membership?.body_closing);
  const note1 = pick(membership?.objection_note_1);
  const note2 = pick(membership?.objection_note_2);
  const note3 = pick(membership?.objection_note_3);
  const isAi = !!((aiBenefits && aiBenefits.length) || aiHeadline || pick(valueReframe) || pick(membership?.section_eyebrow) || body1 || body2 || bodyClosing || note1 || note2 || note3 || pick(membership?.membership_label));

  return (
    <section className="py-20 px-4 sm:px-6" style={{ backgroundColor: "#F0EBE0", borderTop: "1px solid #D8CFC3", backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 31px, rgba(180,170,150,0.08) 31px, rgba(180,170,150,0.08) 32px)" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>

        {/* Leather ledger book — whole card is one hide/delete unit */}
        <EditableElement elementKey="membership:ledger-card" testId="element-membership-ledger">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5 }}>
          <div style={{ background: "linear-gradient(180deg, #5C3D2E 0%, #7A5340 100%)", borderRadius: "8px 8px 0 0", padding: "10px 20px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "inset 0 2px 4px rgba(255,255,255,0.1)" }}>
            <EditableText as="span" style={{ fontSize: 8, fontWeight: 900, letterSpacing: "0.25em", color: "rgba(244,237,221,0.6)", textTransform: "uppercase" as const }} path={["membership", "ledger_label"]} value={(membership as any)?.ledger_label} placeholder="Membership Provisioning Record" />
            <span style={{ fontSize: 7, fontWeight: 700, letterSpacing: "0.15em", color: "rgba(244,237,221,0.35)", textTransform: "uppercase" as const }}>{brandDisplayName}</span>
          </div>
          <div style={{ height: 3, background: "linear-gradient(90deg, var(--color-brand-gold), #E8D5A8, var(--color-brand-gold))" }} />
          <div style={{ backgroundColor: "#FFFDF7", padding: "24px 20px 28px", boxShadow: "0 4px 24px rgba(0,0,0,0.1)", position: "relative" as const, borderLeft: "3px solid #D4C4A0", borderRight: "3px solid #D4C4A0" }}>
            <div style={{ position: "absolute" as const, top: 0, bottom: 0, left: 38, width: 1, backgroundColor: "rgba(200,80,80,0.12)" }} />
            <div style={{ textAlign: "center" as const, marginBottom: 20, paddingBottom: 16, borderBottom: "2px solid #E8DCC8" }}>
              <p style={{ position: "relative" as const, fontSize: 9, fontWeight: 900, letterSpacing: "0.28em", color: "#A0927C", textTransform: "uppercase" as const, marginBottom: 6 }}><EditableText as="span" elementKey="membership:eyebrow" path={["membership", "section_eyebrow"]} value={pick(membership?.section_eyebrow)} placeholder="How the Membership Works" />{" "}<SourceDot source={isAi ? "workflow" : "placeholder"} /></p>
              <h2 style={{ fontSize: 22, lineHeight: 1.25, color: "var(--color-brand-deepnavy)", marginBottom: 6 }} className="font-black" data-testid="text-freshness-value-reframe">{aiHeadline || valueReframe?.trim() ? <EditableText as="span" elementKey="membership:headline" path={["membership", "section_headline"]} value={aiHeadline} placeholder={valueReframe?.trim() || ""} multiline /> : headlineSpinner ? <Loader2 className="inline-block h-5 w-5 animate-spin" style={{ color: "var(--color-brand-deepnavy)", opacity: 0.5 }} /> : <EditableText as="span" elementKey="membership:headline" style={{ whiteSpace: "pre-line" as const }} path={["membership", "section_headline"]} value={aiHeadline} placeholder={v2 ? "Made for you.\nPriced like a membership." : "Built for freshness.\nPriced like a membership."} multiline />}</h2>
              <div style={{ display: "inline-block", border: "1.5px solid var(--color-brand-gold)", borderRadius: 3, padding: "3px 10px", marginTop: 4 }}>
                <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.2em", color: "var(--color-brand-gold)", textTransform: "uppercase" as const }}><EditableText as="span" path={["membership", "membership_label"]} value={pick(membership?.membership_label)} placeholder={v2 ? "Member Essentials" : "Farm to Skin Essentials"} /></span>
              </div>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.75, color: "#5A554F", paddingLeft: 28 }}>
              {body1 || body2 || bodyClosing ? (
                <>
                  {body1 && <EditableText as="p" path={["membership", "body_1"]} value={body1} placeholder="" render={renderBold} multiline style={{ marginBottom: 10 }} />}
                  {body2 && <EditableText as="p" path={["membership", "body_2"]} value={body2} placeholder="" render={renderBold} multiline style={{ marginBottom: 10 }} />}
                  {bodyClosing && <EditableText as="p" path={["membership", "body_closing"]} value={bodyClosing} placeholder="" render={renderBold} multiline style={{ marginBottom: 0, fontWeight: 700, color: "var(--color-brand-navyaccent)" }} />}
                </>
              ) : (
                <>
                  <EditableText as="p" style={{ marginBottom: 10 }} path={["membership", "body_1"]} value={body1 || "Most subscriptions lock you in and ship from a warehouse. **This one works differently.**"} render={renderBold} multiline />
                  <EditableText as="p" style={{ marginBottom: 10 }} path={["membership", "body_2"]} value={body2 || (v2 ? "Your membership tells us **exactly how much to make**, so every order is **made fresh and shipped fast**. No overstock. No guessing. Just **better value** because the model is smarter." : "Your membership tells us **exactly how much to make**, so every batch is **mixed fresh and shipped fast**. No overstock. No guessing. Just **fresher skincare at a lower price** because the model is smarter.")} render={renderBold} multiline />
                  <p style={{ marginBottom: 0, fontWeight: 700, color: "var(--color-brand-navyaccent)" }}>{r.hasDiscount && r.discountPercent ? `You save ${r.discountPercent}%. ` : ""}<EditableText as="span" path={["membership", "body_closing"]} value={bodyClosing || "We make it fresh. Everybody wins."} render={renderBold} multiline /></p>
                </>
              )}
            </div>
          </div>
          <div style={{ background: "linear-gradient(0deg, #5C3D2E 0%, #7A5340 100%)", borderRadius: "0 0 8px 8px", padding: "6px 20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <EditableText as="span" style={{ fontSize: 7, fontWeight: 700, letterSpacing: "0.2em", color: "rgba(244,237,221,0.3)", textTransform: "uppercase" as const }} path={["membership", "ledger_footer_label"]} value={(membership as any)?.ledger_footer_label} placeholder="Member Record" />
          </div>
        </motion.div>
        </EditableElement>

        {/* Membership card — whole card is one hide/delete unit */}
        <EditableElement elementKey="membership:card" testId="element-membership-card">
        <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} style={{ marginTop: 24, borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", background: "linear-gradient(160deg, #FFFDF8 0%, #F8F4EC 50%, var(--color-brand-cream) 100%)", border: "1.5px solid #D8CFC0", position: "relative" as const }}>
          <div style={{ position: "absolute" as const, inset: 0, background: "linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 30%, transparent 70%, rgba(255,255,255,0.15) 100%)", pointerEvents: "none" as const, zIndex: 3 }} />
          <div style={{ background: "linear-gradient(135deg, var(--color-brand-deepnavy) 0%, #243248 100%)", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <LpImage src={logoUrl} alt="Brand logo" path={v2 ? "brand logo" : "/logo-badge.png"} objectFit="contain" style={{ width: 26, height: 26, minHeight: 26, borderRadius: "50%", padding: 0, flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: 10, fontWeight: 900, letterSpacing: "0.18em", color: "var(--color-brand-gold)", textTransform: "uppercase" as const, margin: 0 }}>{brandDisplayName}</p>
                <EditableText as="p" style={{ fontSize: 6, fontWeight: 600, letterSpacing: "0.1em", color: "rgba(244,237,221,0.35)", textTransform: "uppercase" as const, margin: 0, marginTop: 1 }} path={["membership", "membership_tagline"]} value={pick(membership?.membership_tagline)} placeholder={v2 ? "Membership" : "Farm to Skin Membership"} />
              </div>
            </div>
            <div style={{ backgroundColor: v2 ? "color-mix(in srgb, var(--color-brand-greenaccent) 20%, transparent)" : "rgba(45,122,79,0.2)", borderRadius: 3, padding: "2px 6px", border: v2 ? "1px solid color-mix(in srgb, var(--color-brand-greenaccent) 30%, transparent)" : "1px solid rgba(45,122,79,0.3)" }}>
              <EditableText as="span" style={{ fontSize: 7, fontWeight: 900, letterSpacing: "0.12em", color: v2 ? "var(--color-brand-greenaccent)" : "#4CAF76", textTransform: "uppercase" as const }} path={["membership", "status_label"]} value={(membership as any)?.status_label} placeholder="Active" />
            </div>
          </div>
          <div style={{ height: 2, background: "linear-gradient(90deg, var(--color-brand-gold), #E8D5A8, var(--color-brand-gold), #E8D5A8, var(--color-brand-gold))" }} />
          <div style={{ padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #E8E0D2" }}>
            {memberFields.map(f => (
              <div key={f.labelKey} style={{ textAlign: "center" as const }}>
                <EditableText as="p" style={{ fontSize: 6, fontWeight: 800, letterSpacing: "0.18em", color: "#A89C8B", textTransform: "uppercase" as const, margin: 0 }} path={["membership", f.labelKey]} value={(membership as any)?.[f.labelKey]} placeholder={f.labelPlaceholder} />
                <EditableText as="p" style={{ fontSize: 12, fontWeight: 700, color: f.color, margin: "1px 0 0", fontFamily: f.mono ? "monospace" : undefined }} path={["membership", f.valKey]} value={f.valRaw} placeholder={f.valPlaceholder} />
              </div>
            ))}
          </div>
          <div style={{ padding: "8px 16px 6px" }}>
            <EditableText as="p" style={{ fontSize: 6, fontWeight: 900, letterSpacing: "0.2em", color: "#A89C8B", textTransform: "uppercase" as const, margin: "0 0 6px 0" }} path={["membership", "benefits_label"]} value={(membership as any)?.benefits_label} placeholder="Member Benefits" />
            {MEMBER_BENEFITS.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: i < 4 ? "1px solid #EDE7DA" : "none" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", border: "1.5px solid var(--color-brand-gold)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, backgroundColor: "rgba(196,162,101,0.06)" }}>
                  <span style={{ fontSize: item.icon.length > 2 ? 6 : 8, fontWeight: 900, color: "var(--color-brand-gold)" }}>{item.icon}</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap" as const, gap: "0 4px" }}>
                  {item.storedIndex >= 0
                    ? <EditableText as="span" style={{ fontSize: 11, fontWeight: 800, color: "var(--color-brand-deepnavy)" }} path={["membership", "benefits", item.storedIndex, "label"]} value={item.labelRaw} placeholder="" />
                    : <span style={{ fontSize: 11, fontWeight: 800, color: "var(--color-brand-deepnavy)" }}>{item.label}</span>}
                  {item.storedIndex >= 0
                    ? <EditableText as="span" style={{ fontSize: 9, color: "#9A8E7E" }} path={["membership", "benefits", item.storedIndex, "note"]} value={item.noteRaw} placeholder="" />
                    : <span style={{ fontSize: 9, color: "#9A8E7E" }}>{item.note}</span>}
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding: "6px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <EditableText as="span" style={{ fontSize: 6, fontWeight: 700, letterSpacing: "0.2em", color: "var(--color-brand-gold)", opacity: 0.5, textTransform: "uppercase" as const }} path={["membership", "location_label"]} value={pick(membership?.location_label)} placeholder={v2 ? "" : "Temecula, CA"} />
            <EditableText as="span" style={{ fontSize: 6, fontWeight: 700, letterSpacing: "0.15em", color: "var(--color-brand-gold)", opacity: 0.5, textTransform: "uppercase" as const }} path={["membership", "membership_label"]} value={pick(membership?.membership_label)} placeholder={v2 ? "Member Essentials" : "Farm to Skin Essentials"} />
          </div>
        </motion.div>
        </EditableElement>

        {/* Bulletin board closing — whole board is one hide/delete unit */}
        <EditableElement elementKey="membership:bulletin-board" testId="element-membership-bulletin">
        <div style={{ marginTop: 20, backgroundColor: "var(--color-brand-gold)", borderRadius: 8, padding: 10, boxShadow: "inset 0 2px 8px rgba(0,0,0,0.15)" }}>
          {[
            { bg: "#FFFDF7", rotate: "-0.5deg", pinColor: "#C0392B", pinPos: { top: -4, left: "50%", marginLeft: -5 }, text: <EditableText as="span" path={["membership", "objection_note_1"]} value={note1 || "**Skip, pause, or cancel anytime.** No contracts, no commitments, no fine print."} render={renderBold} multiline /> },
            { bg: "#F0E8D4", rotate: "0.4deg",  pinColor: "var(--color-brand-greenaccent)", pinPos: { top: -4, right: 20 },                  text: <EditableText as="span" style={{ fontSize: 13, fontWeight: 800, fontStyle: "italic" as const, color: "var(--color-brand-navyaccent)" }} path={["membership", "objection_note_2"]} value={note2 || "45-day money-back guarantee on every order."} multiline /> },
          ].map((note, ni) => (
            <div key={ni} style={{ position: "relative" as const, backgroundColor: note.bg, borderRadius: 3, padding: "12px 14px", marginBottom: 8, boxShadow: "1px 2px 6px rgba(0,0,0,0.1)", transform: `rotate(${note.rotate})` }}>
              <div style={{ position: "absolute" as const, ...note.pinPos, width: 10, height: 10, borderRadius: "50%", background: `radial-gradient(circle at 3px 3px, ${note.pinColor}, ${note.pinColor}cc)`, boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
              <p style={{ fontSize: 12, lineHeight: 1.6, color: "#5A554F", margin: 0, textAlign: "center" as const, paddingTop: 4 }}>{note.text}</p>
            </div>
          ))}
          <div style={{ position: "relative" as const, backgroundColor: "var(--color-brand-deepnavy)", borderRadius: 3, padding: "12px 16px", boxShadow: "1px 2px 8px rgba(0,0,0,0.15)", transform: "rotate(-0.3deg)" }}>
            <div style={{ position: "absolute" as const, top: -4, left: 16, width: 10, height: 10, borderRadius: "50%", background: "radial-gradient(circle at 3px 3px, #F5D76E, var(--color-brand-gold))", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
            <div style={{ position: "absolute" as const, top: -4, right: 16, width: 10, height: 10, borderRadius: "50%", background: "radial-gradient(circle at 3px 3px, #F5D76E, var(--color-brand-gold))", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
            <p style={{ fontSize: 12, lineHeight: 1.7, color: "var(--color-brand-cream)", fontWeight: 600, margin: 0, textAlign: "center" as const, paddingTop: 4 }}>
              {note3 ? renderBold(note3) : (v2 ? <>Not a trap. Not a warehouse box.<br /><strong style={{ color: "var(--color-brand-gold)" }}>Made to order. Shipped fast. Guaranteed.</strong></> : <>Not a trap. Not a warehouse box.<br /><strong style={{ color: "var(--color-brand-gold)" }}>Ranch-made. Shipped fresh. Guaranteed.</strong></>)}
            </p>
          </div>
        </div>
        </EditableElement>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   S11 – WHAT'S THE ALTERNATIVE
───────────────────────────────────────────── */

const VILLAIN_CARDS = [
  { img: "/big-pharma-action-figure.jpeg",   alt: "Big Pharma Skincare action figure",  title: "Big Pharma Skincare",     body: <>Petroleum jelly, synthetic fragrance, and a marketing budget bigger than their ingredient list. Optimized for <strong style={{ color: "var(--color-brand-deepnavy)" }}>shelf life</strong>, not <strong style={{ color: "var(--color-brand-deepnavy)" }}>your skin</strong>.</> },
  { img: "/pe-rollup-ceo.jpeg",               alt: "The Roll-Up CEO action figure",      title: "Private Equity Brands",   body: <>Cost-cutting scissors included. Buy a brand, gut the formula, keep the label. Optimized for <strong style={{ color: "var(--color-brand-deepnavy)" }}>margins</strong>, not <strong style={{ color: "var(--color-brand-deepnavy)" }}>your face</strong>.</> },
  { img: "/foreign-outsourcer.jpeg",          alt: "The Outsourcer action figure",       title: "Foreign-Made Formulas",   body: <>Sourced overseas, made by strangers. Private-label formula, offshore factory. You'll never know <strong style={{ color: "var(--color-brand-deepnavy)" }}>who made it</strong> or <strong style={{ color: "var(--color-brand-deepnavy)" }}>what's really in it</strong>.</> },
  { img: "/influencer-viral-founder.jpeg",    alt: "The Viral Founder action figure",    title: "Influencer Brands",       body: <>Ring light, PR box, and a script card. Great at going viral. Less great at explaining <strong style={{ color: "var(--color-brand-deepnavy)" }}>what's actually in the bottle</strong>.</> },
  { img: "/dept-store-prestige.jpeg",         alt: "The Prestige Seller action figure",  title: "Department Store Brands", body: <>Oversized price tag, velvet gift bag, and a sales script. You're paying for <strong style={{ color: "var(--color-brand-deepnavy)" }}>the prestige</strong>, not <strong style={{ color: "var(--color-brand-deepnavy)" }}>the formula</strong>.</> },
];

// Brand-neutral competitor cards for Long Form v2 — generic archetypes with no
// images (dashed placeholder shows) used before AI populates. v1 never uses these.
const VILLAIN_CARDS_V2 = [
  { img: "", alt: "Mass-market brand",       title: "Mass-Market Brands",     body: <>Cheap inputs, big ad budgets, and a product built for <strong style={{ color: "var(--color-brand-deepnavy)" }}>margins</strong>, not <strong style={{ color: "var(--color-brand-deepnavy)" }}>you</strong>.</> },
  { img: "", alt: "Private equity brand",    title: "Private Equity Brands",  body: <>Buy a brand, cut the costs, keep the label. Optimized for <strong style={{ color: "var(--color-brand-deepnavy)" }}>the spreadsheet</strong>, not <strong style={{ color: "var(--color-brand-deepnavy)" }}>the product</strong>.</> },
  { img: "", alt: "Overseas manufacturer",   title: "Overseas-Made Products", body: <>Made by strangers in a factory you'll never see. You never really know <strong style={{ color: "var(--color-brand-deepnavy)" }}>who made it</strong> or <strong style={{ color: "var(--color-brand-deepnavy)" }}>what's in it</strong>.</> },
  { img: "", alt: "Influencer brand",        title: "Influencer Brands",      body: <>Ring light, PR box, and a script card. Great at going viral, less great at explaining <strong style={{ color: "var(--color-brand-deepnavy)" }}>what's actually inside</strong>.</> },
  { img: "", alt: "Prestige brand",          title: "Prestige Brands",        body: <>Oversized price tag and a sales script. You're paying for <strong style={{ color: "var(--color-brand-deepnavy)" }}>the prestige</strong>, not <strong style={{ color: "var(--color-brand-deepnavy)" }}>the quality</strong>.</> },
];

function AlternativesSection({
  closingChallenge,
  loading = false,
  alternatives,
  alternativesLoading = false,
  images,
  imagesLoading = false,
  receipt,
  bundleImage,
  bundleProductPhotos,
  brandName,
  checkoutUrl,
  onRetry,
}: {
  closingChallenge?: string;
  loading?: boolean;
  alternatives?: AlternativesCopy;
  alternativesLoading?: boolean;
  images?: LpImgSlot[];
  imagesLoading?: boolean;
  receipt?: LpReceipt;
  bundleImage?: string | null;
  bundleProductPhotos?: string[];
  brandName?: string | null;
  checkoutUrl?: string | null;
  onRetry?: (slotKey: string) => void;
}) {
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  const v2 = useLpV2();
  const receiptCtaOverride = useLpMediaOverride("product:alt-cta");
  const receiptCtaImage = receiptCtaOverride ?? bundleImage;
  const eyebrow = pick(alternatives?.section_eyebrow) || "The Alternative";
  const heroHeadline = pick(alternatives?.hero_headline) || (v2 ? "Why choose us?" : "Why choose Papabear?");
  const heroSub = pick(alternatives?.hero_subheadline);
  const aiDiffEntries = (alternatives?.hero_differentiators ?? [])
    .map((d, idx) => ({ d, idx }))
    .filter(({ d }) => typeof d === "string" && d.trim());
  const aiDiffs = aiDiffEntries.map((e) => e.d);
  const heroDiffEntries = aiDiffEntries.length
    ? aiDiffEntries.map(({ d, idx }) => ({ value: (d as string).trim(), placeholder: (d as string).trim(), index: idx }))
    : (v2 ? ["Family-owned", "American-made", "Real quality", "Built to last"] : ["Family-owned", "American-made", "Real ingredients", "Barrier support"]).map((t, i) => ({ value: "", placeholder: t, index: i }));
  const competitorIntro = pick(alternatives?.competitor_intro) || "Now meet the competition.";
  const aiCardEntries = (alternatives?.competitor_cards ?? [])
    .map((c, idx) => ({ c, idx }))
    .filter(({ c }) => pick(c?.title) || pick(c?.body));
  const aiCards = aiCardEntries.map((e) => e.c);
  const competitorCards = (v2 ? VILLAIN_CARDS_V2 : VILLAIN_CARDS).map((card, i) => {
    const entry = aiCardEntries[i];
    return {
      img: card.img,
      alt: card.alt,
      title: pick(entry?.c?.title) || card.title,
      titleRaw: pick(entry?.c?.title),
      titleFallback: card.title,
      body: entry?.c?.body ? renderBold(pick(entry.c.body)) : card.body,
      bodyRaw: pick(entry?.c?.body),
      storedIndex: entry ? entry.idx : -1,
      hidden: entry?.c?.hidden,
    };
  });
  const themLabel = pick(alternatives?.comparison_them_label) || "Them";
  const usLabel = pick(alternatives?.comparison_us_label) || (v2 ? "Us" : "Papabear");
  const aiThemEntries = (alternatives?.comparison_them ?? [])
    .map((x, idx) => ({ x, idx }))
    .filter(({ x }) => typeof x === "string" && x.trim());
  const aiThem = aiThemEntries.map((e) => e.x);
  const themEntries = aiThemEntries.length
    ? aiThemEntries.map(({ x, idx }) => ({ value: (x as string).trim(), placeholder: (x as string).trim(), index: idx }))
    : ["Lab-made fillers", "Overseas factories", "Board-approved", "Trend-chasing", "Shelf-life first"].map((t, i) => ({ value: "", placeholder: t, index: i }));
  const aiUsEntries = (alternatives?.comparison_us ?? [])
    .map((x, idx) => ({ x, idx }))
    .filter(({ x }) => typeof x === "string" && x.trim());
  const aiUs = aiUsEntries.map((e) => e.x);
  const usEntries = aiUsEntries.length
    ? aiUsEntries.map(({ x, idx }) => ({ value: (x as string).trim(), placeholder: (x as string).trim(), index: idx }))
    : (v2 ? ["Premium quality", "Made in the USA", "Family-owned", "Built with care", "Honest pricing"] : ["Whole-food ingredients", "Temecula, CA ranch", "Family-owned", "Function-driven", "Made fresh weekly"]).map((t, i) => ({ value: "", placeholder: t, index: i }));
  const closing1 = pick(alternatives?.closing_line_1);
  const closing2 = pick(alternatives?.closing_line_2);
  const heroSlot = resolveImgSlot(images, "brand-hero", imagesLoading);
  const isAi = !!((aiCards && aiCards.length) || (aiDiffs && aiDiffs.length) || (aiThem && aiThem.length) || (aiUs && aiUs.length) || pick(closingChallenge) || pick(alternatives?.section_eyebrow) || pick(alternatives?.hero_headline) || heroSub || pick(alternatives?.competitor_intro) || closing1 || closing2);
  return (
    <section className="py-20 px-4 sm:px-6" style={{ backgroundColor: "#FAFAF7", borderTop: "1px solid #E8E0D4" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>

        {/* Hero card */}
        <motion.div style={{ marginBottom: 28 }} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5 }}>
          <div style={{ borderRadius: 16, overflow: "hidden", background: "linear-gradient(160deg, var(--color-brand-navyaccent) 0%, #14294D 100%)", boxShadow: "0 8px 32px rgba(27,58,107,0.3)", position: "relative" as const }}>
            <div style={{ position: "absolute" as const, top: -40, right: -40, width: 120, height: 120, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.04)" }} />
            <div style={{ padding: "28px 24px 20px", textAlign: "center" as const, position: "relative" as const, zIndex: 1 }}>
              <p style={{ position: "relative" as const, fontSize: 10, fontWeight: 900, letterSpacing: "0.25em", color: "rgba(244,237,221,0.5)", textTransform: "uppercase" as const, marginBottom: 10 }}><EditableText as="span" elementKey="alternatives:eyebrow" path={["alternatives", "section_eyebrow"]} value={pick(alternatives?.section_eyebrow)} placeholder="The Alternative" />{" "}<SourceDot source={isAi ? "workflow" : "placeholder"} /></p>
              <h2 style={{ fontSize: 28, lineHeight: 1.2, color: "#FFFFFF", fontWeight: 900, marginBottom: 14 }}><EditableText as="span" elementKey="alternatives:headline" path={["alternatives", "hero_headline"]} value={pick(alternatives?.hero_headline)} placeholder={v2 ? "Why choose us?" : "Why choose Papabear?"} multiline /></h2>
              {heroSub ? (
                <p style={{ fontSize: 16, fontWeight: 800, color: "#FFFFFF", marginTop: 2, marginBottom: 0 }}><EditableText as="span" elementKey="alternatives:subheadline" path={["alternatives", "hero_subheadline"]} value={heroSub} placeholder="" multiline /></p>
              ) : (
                <>
                  <p style={{ fontSize: 13, lineHeight: 1.7, color: "rgba(255,255,255,0.55)", marginBottom: 6 }}>
                    <EditableText as="span" path={["alternatives", "hero_optimize_intro"]} value={(alternatives as any)?.hero_optimize_intro} placeholder="Other brands optimize for" />{" "}
                    {["scale", "shelf life", "trends", "investor returns"].map(w => <span key={w} style={{ textDecoration: "line-through", color: "var(--color-brand-coral)" }}>{w} </span>)}
                  </p>
                  <p style={{ fontSize: 16, fontWeight: 800, color: "#FFFFFF", marginTop: 2, marginBottom: 0 }}><EditableText as="span" path={["alternatives", "hero_optimized_line"]} value={(alternatives as any)?.hero_optimized_line} placeholder="We're optimized for your skin." multiline /></p>
                </>
              )}
            </div>
            <div style={{ padding: "0 24px 24px", position: "relative" as const, zIndex: 1 }}>
              <EditableElement elementKey="alternatives:brand-hero-image" toolbarSide="left" testId="element-alternatives-brand-hero" className="mx-auto max-w-full md:max-w-[280px]">
                <div className="relative group/lpimg" style={{ borderRadius: 12, overflow: "hidden", border: "2px solid rgba(255,255,255,0.15)" }}>
                  <SlotImage slot={heroSlot} fallbackPath={v2 ? "brand hero image" : "/papabear-action-figure.jpeg"} alt={v2 ? "Brand hero image" : "Papabear action figure"} rounded={false} style={{ height: 280 }} onRetry={onRetry} overrideKey="alternativesImages:brand-hero" regenKey="brand-hero" />
                </div>
              </EditableElement>
            </div>
            <div style={{ padding: "16px 24px 20px", borderTop: "1px solid rgba(255,255,255,0.1)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", position: "relative" as const, zIndex: 1 }}>
              {heroDiffEntries.map((item) => (
                <div key={item.index} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "var(--color-brand-goldaccent)", flexShrink: 0 }} />
                  <EditableText as="span" path={["alternatives", "hero_differentiators", item.index]} value={item.value} placeholder={item.placeholder} style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", fontWeight: 600 }} />
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Villain action figure cards */}
        <motion.div style={{ marginBottom: 36 }} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.45 }}>
          <h3 className="font-black" style={{ fontSize: 20, lineHeight: 1.25, color: "var(--color-brand-navyaccent)", marginBottom: 16 }}><EditableText as="span" path={["alternatives", "competitor_intro"]} value={pick(alternatives?.competitor_intro)} placeholder="Now meet the competition." multiline /></h3>
          <div style={{ display: "flex", flexDirection: "column" as const, gap: 16 }}>
            {competitorCards.map((card, ci) => {
              const editable = card.storedIndex >= 0;
              const cardBody = (
                <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #E0DCD4", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                  <div className="relative group/lpimg mx-auto max-w-full md:max-w-[280px]">
                    <SlotImage slot={resolveImgSlot(images, `competitor-${ci + 1}`, imagesLoading)} fallbackPath={v2 ? `competitor ${ci + 1} photo` : card.img} alt={card.alt} rounded={false} style={{ height: 260 }} onRetry={onRetry} overrideKey={`alternativesImages:competitor-${ci + 1}`} regenKey={`competitor-${ci + 1}`} />
                  </div>
                  <div style={{ position: "relative" as const, padding: "16px 18px 16px 50px", backgroundColor: "#FFFDF6", borderTop: "2px dashed #D8CFC3" }}>
                    <div style={{ position: "absolute" as const, left: 16, top: 18, width: 24, height: 24, borderRadius: "50%", backgroundColor: "#C0392B", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: 13, color: "#FFFFFF", fontWeight: 900 }}>!</span>
                    </div>
                    {editable
                      ? <EditableText as="p" path={["alternatives", "competitor_cards", card.storedIndex, "title"]} value={card.titleRaw} placeholder={card.titleFallback} style={{ fontSize: 14, fontWeight: 800, color: "#C0392B", marginBottom: 5, letterSpacing: "0.03em", textTransform: "uppercase" as const }} />
                      : <p style={{ fontSize: 14, fontWeight: 800, color: "#C0392B", marginBottom: 5, letterSpacing: "0.03em", textTransform: "uppercase" as const }}>{card.title}</p>}
                    {editable
                      ? <EditableText as="p" path={["alternatives", "competitor_cards", card.storedIndex, "body"]} value={card.bodyRaw} placeholder="" render={renderBold} multiline style={{ fontSize: 13, lineHeight: 1.6, color: "#4A4540", margin: 0 }} />
                      : <p style={{ fontSize: 13, lineHeight: 1.6, color: "#4A4540", margin: 0 }}>{card.body}</p>}
                  </div>
                </div>
              );
              return editable ? (
                <EditableItem key={card.title} path={["alternatives", "competitor_cards"]} index={card.storedIndex} count={alternatives?.competitor_cards?.length ?? 0} hidden={card.hidden} allowDelete toolbarSide="left" testId={`alternatives-card-${card.storedIndex}`}>
                  {cardBody}
                </EditableItem>
              ) : (
                // Attach the key to the card's own root element instead of a
                // React.Fragment: the dev cartographer plugin injects a
                // `data-replit-metadata` prop onto JSX nodes, and a Fragment
                // rejects any prop other than `key`/`children` (console warning).
                React.cloneElement(cardBody, { key: card.title })
              );
            })}
          </div>
        </motion.div>

        {/* Comparison grid — whole card is one hide/delete unit */}
        <EditableElement elementKey="alternatives:comparison-grid" testId="element-alternatives-comparison">
        <motion.div style={{ marginBottom: 32 }} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.45 }}>
          <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid #E0DCD4", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
              <div style={{ padding: "20px 16px", backgroundColor: "#FFF5F5", borderRight: "1px solid #E0DCD4" }}>
                <p style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.2em", color: "#C0392B", textTransform: "uppercase" as const, marginBottom: 12, textAlign: "center" as const }}><EditableText as="span" path={["alternatives", "comparison_them_label"]} value={pick(alternatives?.comparison_them_label)} placeholder="Them" /></p>
                {themEntries.map((item) => (
                  <div key={item.index} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: "#C0392B" }}>✕</span>
                    <EditableText as="span" path={["alternatives", "comparison_them", item.index]} value={item.value} placeholder={item.placeholder} style={{ fontSize: 11, color: "#7A6E66", lineHeight: 1.3 }} multiline />
                  </div>
                ))}
              </div>
              <div style={{ padding: "20px 16px", backgroundColor: "#F0F7F4" }}>
                <p style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.2em", color: "var(--color-brand-greenaccent)", textTransform: "uppercase" as const, marginBottom: 12, textAlign: "center" as const }}><EditableText as="span" path={["alternatives", "comparison_us_label"]} value={pick(alternatives?.comparison_us_label)} placeholder={v2 ? "Us" : "Papabear"} /></p>
                {usEntries.map((item) => (
                  <div key={item.index} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: "var(--color-brand-greenaccent)", fontWeight: 800 }}>+</span>
                    <EditableText as="span" path={["alternatives", "comparison_us", item.index]} value={item.value} placeholder={item.placeholder} style={{ fontSize: 11, color: "#2A4A38", fontWeight: 600, lineHeight: 1.3 }} multiline />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
        </EditableElement>

        {/* Closing banner */}
        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.45 }}>
          <div style={{ background: "linear-gradient(135deg, var(--color-brand-navyaccent) 0%, #2A4F8A 100%)", borderRadius: 14, padding: "28px 24px", textAlign: "center" as const, position: "relative" as const, overflow: "hidden" }} data-testid="text-alternatives-closing-challenge">
            {closingChallenge?.trim() ? (
              <EditableText as="p" path={["quotes", "closing_challenge", "quote"]} value={closingChallenge.trim()} placeholder="" multiline style={{ fontSize: 17, lineHeight: 1.5, color: "#FFFFFF", fontWeight: 800, margin: 0, position: "relative" as const, zIndex: 1 }} />
            ) : closing1 || closing2 ? (
              <>
                {closing1 && <EditableText as="p" path={["alternatives", "closing_line_1"]} value={closing1} placeholder="" multiline style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(255,255,255,0.75)", marginBottom: 6, position: "relative" as const, zIndex: 1 }} />}
                {closing2 && <EditableText as="p" path={["alternatives", "closing_line_2"]} value={closing2} placeholder="" multiline style={{ fontSize: 17, lineHeight: 1.5, color: "#FFFFFF", fontWeight: 800, margin: 0, position: "relative" as const, zIndex: 1 }} />}
              </>
            ) : loading || alternativesLoading ? (
              <div style={{ display: "flex", justifyContent: "center", position: "relative" as const, zIndex: 1 }} data-testid="loading-alternatives-closing-challenge">
                <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#FFFFFF" }} />
              </div>
            ) : (
              <>
                <EditableText as="p" path={["alternatives", "closing_line_1"]} value={closing1} placeholder={v2 ? "You can buy from a trend, a conglomerate, or a boardroom." : "You can buy skincare from a trend, a conglomerate, or a boardroom."} multiline style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(255,255,255,0.75)", marginBottom: 6, position: "relative" as const, zIndex: 1 }} />
                <EditableText as="p" path={["alternatives", "closing_line_2"]} value={closing2} placeholder="Or from a family that actually stands behind it." multiline style={{ fontSize: 17, lineHeight: 1.5, color: "#FFFFFF", fontWeight: 800, margin: 0, position: "relative" as const, zIndex: 1 }} />
              </>
            )}
          </div>
        </motion.div>

        {/* Receipt buy box */}
        <motion.div className="mt-8" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5 }}>
          <EditableElement elementKey="receipt:bundle-image" toolbarSide="left" testId="element-receipt-bundle-image">
            <div className="group/lpimg relative" style={{ borderRadius: 14, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, overflow: "hidden", border: "2px solid #D8CFC3", borderBottom: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
              <LpImage src={receiptCtaImage} alt="Product bundle" path={v2 ? "product bundle photo" : "/bundle-flatlay.jpeg"} rounded={false} objectFit="contain" style={{ height: 240, backgroundColor: "#FAF6EE" }} />
              <LpImageActions slotKey="product:alt-cta" productPhotos={bundleProductPhotos} revertLabel="Use default" revertTitle="Use the default product photo" />
            </div>
          </EditableElement>
          <FarmstandReceiptBox receipt={r} brandName={brandName} checkoutUrl={v2 ? (checkoutUrl ?? "") : undefined} />
        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   S6 – FUNCTIONAL SKINCARE
───────────────────────────────────────────── */

const PILLARS = [
  { num: "01", title: "Supports, Not Masks",         text: "Real ingredients are bioidentical to your skin's own oils. They feed your skin barrier instead of coating over it." },
  { num: "02", title: "Built for the Long Run",       text: "Most products stop working after a few weeks because they were never fixing anything. Full-spectrum ingredients compound over time." },
  { num: "03", title: "Five Ingredients You Can Read", text: "Tallow, raw honey, beeswax, olive oil, essential oils. Every ingredient does real work. Nothing your skin has to fight against." },
  { num: "04", title: "Healthy Skin Looks Best",      text: "You don't need to fake good skin. When it's actually nourished with whole, natural ingredients, it shows on its own." },
];

// Brand-neutral philosophy pillars for Long Form v2 — category-agnostic copy used
// before AI-generated pillars populate. v1 never references this.
const PILLARS_V2 = [
  { num: "01", title: "Built to Deliver",        text: "Every detail is designed to do real work for you, not just look good on a shelf." },
  { num: "02", title: "Made for the Long Run",   text: "This isn't a quick fix. It's built to keep performing the longer you use it." },
  { num: "03", title: "Nothing You Can't Trust", text: "Honest materials, an honest process. Everything here earns its place." },
  { num: "04", title: "Quality You Can Feel",    text: "When something is genuinely well made, you notice it from the very first use." },
];

function FunctionalSkincareSection({
  philosophy,
  philosophyLoading = false,
  images,
  imagesLoading = false,
  onRetry,
}: {
  philosophy?: PhilosophyCopy;
  philosophyLoading?: boolean;
  images?: LpImgSlot[];
  imagesLoading?: boolean;
  onRetry?: (slotKey: string) => void;
}) {
  const v2 = useLpV2();
  const basePillars = v2 ? PILLARS_V2 : PILLARS;
  const eyebrow = pick(philosophy?.section_eyebrow) || "A Different Approach";
  const aiHeadline = pick(philosophy?.section_headline);
  const body1 = pick(philosophy?.section_body_1);
  const body2 = pick(philosophy?.section_body_2);
  const cardLabel = pick(philosophy?.pillar_card_label) || "How it all works together";
  const aiPillarEntries = (philosophy?.pillars ?? [])
    .map((p, idx) => ({ p, idx }))
    .filter(({ p }) => pick(p?.title) || pick(p?.body));
  const aiPillars = aiPillarEntries.map((e) => e.p);
  const pillars = aiPillarEntries.length
    ? aiPillarEntries.map(({ p, idx }, i) => ({
        num: pick(p?.num) || basePillars[i % basePillars.length].num,
        title: pick(p?.title) || basePillars[i % basePillars.length].title,
        titleRaw: pick(p?.title),
        titleFallback: basePillars[i % basePillars.length].title,
        text: pick(p?.body) || basePillars[i % basePillars.length].text,
        textRaw: pick(p?.body),
        textFallback: basePillars[i % basePillars.length].text,
        storedIndex: idx,
      }))
    : basePillars.map((p) => ({ num: p.num, title: p.title, titleRaw: "", titleFallback: p.title, text: p.text, textRaw: "", textFallback: p.text, storedIndex: -1 }));
  const aiFooterEntries = (philosophy?.pillar_footer_words ?? [])
    .map((w, idx) => ({ w, idx }))
    .filter(({ w }) => typeof w === "string" && w.trim());
  const aiFooter = aiFooterEntries.map((e) => e.w);
  const footerEntries = aiFooterEntries.length
    ? aiFooterEntries.map(({ w, idx }) => ({ value: (w as string).trim(), placeholder: (w as string).trim(), index: idx }))
    : (v2 ? ["Quality", "Value", "Trust", "Results"] : ["Support", "Sustain", "Nourish", "Thrive"]).map((t, i) => ({ value: "", placeholder: t, index: i }));
  const lifestyleSlot = resolveImgSlot(images, "brand-lifestyle", imagesLoading);
  const isAi = !!(aiHeadline || body1 || body2 || (aiPillars && aiPillars.length) || (aiFooter && aiFooter.length) || pick(philosophy?.section_eyebrow) || pick(philosophy?.pillar_card_label));
  return (
    <section id="functional-skincare" className="py-24 px-4 sm:px-6 bg-white">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <EditableElement elementKey="philosophy:lifestyle-image" toolbarSide="left" testId="element-philosophy-lifestyle-image">
          <div className="aspect-[0.85] rounded-3xl overflow-hidden relative group/lpimg">
            <SlotImage slot={lifestyleSlot} fallbackPath={v2 ? "lifestyle image" : "/farm-cattle-sunlight.jpg"} alt={v2 ? "Brand lifestyle" : "Farm lifestyle"} rounded={false} style={{ height: "100%" }} onRetry={onRetry} overrideKey="philosophyImages:brand-lifestyle" regenKey="brand-lifestyle" />
          </div>
        </EditableElement>
        <div>
          <p style={{ position: "relative" as const, fontSize: 10, fontWeight: 900, letterSpacing: "0.22em", color: "#9A8E80", textTransform: "uppercase" as const, marginBottom: 10 }}><EditableText as="span" elementKey="philosophy:eyebrow" path={["philosophy", "section_eyebrow"]} value={pick(philosophy?.section_eyebrow)} placeholder="A Different Approach" />{" "}<SourceDot source={isAi ? "workflow" : "placeholder"} /></p>
          {aiHeadline ? (
            <EditableText as="h2" elementKey="philosophy:headline" className="text-4xl md:text-5xl font-black text-brand-navy uppercase tracking-tight mb-4" path={["philosophy", "section_headline"]} value={aiHeadline} placeholder="" multiline />
          ) : (
            <EditableText as="h2" elementKey="philosophy:headline" className="text-4xl md:text-5xl font-black text-brand-navy uppercase tracking-tight mb-4" style={{ whiteSpace: "pre-line" as const }} path={["philosophy", "section_headline"]} value={aiHeadline} placeholder={v2 ? "Built\nDifferent" : "Functional\nSkin Care"} multiline />
          )}
          {body1 || body2 ? (
            <>
              {body1 && <EditableText as="p" className="text-lg text-brand-text mb-6 max-w-lg" path={["philosophy", "section_body_1"]} value={body1} placeholder="" multiline />}
              {body2 && <EditableText as="p" className="text-lg text-brand-text mb-12 max-w-lg" path={["philosophy", "section_body_2"]} value={body2} placeholder="" multiline />}
            </>
          ) : (
            <>
              <EditableText as="p" className="text-lg text-brand-text mb-6 max-w-lg" path={["philosophy", "section_body_1"]} value={body1} placeholder={v2 ? "Most products are designed to make a sale, not to make your life better. Quick fixes and shortcuts. They want the purchase. They're not thinking about you a year from now." : "Most skincare is designed to make a sale, not to make your skin healthier. Quick fixes, synthetic coatings, temporary relief. They want the purchase. They're not thinking about your skin next year."} multiline />
              <EditableText as="p" className="text-lg text-brand-text mb-12 max-w-lg" path={["philosophy", "section_body_2"]} value={body2} placeholder={v2 ? "This is different. It actually does what it promises and keeps doing it long-term. Real quality is what stands out. Everything else is just pretending." : "This is different. Functional skincare means your skin actually gets what it needs to be healthy long-term. Healthy skin is what looks the best. Everything else is just pretending."} multiline />
            </>
          )}

          <EditableElement elementKey="philosophy:pillar-card" testId="element-philosophy-pillar">
          <div style={{ borderRadius: 14, overflow: "hidden", background: "linear-gradient(170deg, #2C3E2D 0%, #1E2B1F 40%, #1A241B 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 32px rgba(0,0,0,0.25)", position: "relative" as const }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: "0.22em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase" as const }}><EditableText as="span" path={["philosophy", "pillar_card_label"]} value={pick(philosophy?.pillar_card_label)} placeholder="How it all works together" /></span>
              <div style={{ display: "flex", gap: 5 }}>
                {[0.4, 0.6, 0.9].map((op, i) => (
                  <motion.div key={i} animate={{ opacity: [op * 0.6, op, op * 0.6] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }} style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: `rgba(180,220,180,${op})` }} />
                ))}
              </div>
            </div>
            <div style={{ padding: "6px 20px 20px" }}>
              {pillars.map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-20px" }} transition={{ duration: 0.45, delay: i * 0.1 }} style={{ display: "flex", gap: 14, padding: "16px 0", borderBottom: i < pillars.length - 1 ? "1px dashed rgba(255,255,255,0.1)" : "none", alignItems: "flex-start" }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(180,220,180,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 900, color: "rgba(180,220,180,0.7)", fontFamily: "monospace" }}>{p.num}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {p.storedIndex >= 0
                      ? <EditableText as="p" style={{ fontSize: 15, fontWeight: 800, color: "#E8E4DC", marginBottom: 4, lineHeight: 1.3 }} path={["philosophy", "pillars", p.storedIndex, "title"]} value={p.titleRaw} placeholder={p.titleFallback} multiline />
                      : <p style={{ fontSize: 15, fontWeight: 800, color: "#E8E4DC", marginBottom: 4, lineHeight: 1.3 }}>{p.title}</p>}
                    {p.storedIndex >= 0
                      ? <EditableText as="p" style={{ fontSize: 12.5, color: "rgba(228,224,216,0.6)", margin: 0, lineHeight: 1.6 }} path={["philosophy", "pillars", p.storedIndex, "body"]} value={p.textRaw} placeholder={p.textFallback} multiline />
                      : <p style={{ fontSize: 12.5, color: "rgba(228,224,216,0.6)", margin: 0, lineHeight: 1.6 }}>{p.text}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "center", gap: 8, alignItems: "center" }}>
              {footerEntries.map((item, i) => (
                <span key={item.index} style={{ display: "contents" }}>
                  <EditableText as="span" style={{ fontSize: 10, fontWeight: 700, color: "rgba(180,220,180,0.6)", letterSpacing: "0.05em" }} path={["philosophy", "pillar_footer_words", item.index]} value={item.value} placeholder={item.placeholder} />
                  {i < footerEntries.length - 1 && <span style={{ fontSize: 10, color: "rgba(180,220,180,0.3)" }}>&rarr;</span>}
                </span>
              ))}
            </div>
          </div>
          </EditableElement>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   S9 – WHAT MAKES THIS DIFFERENT
───────────────────────────────────────────── */

const DIFFERENTIATORS = [
  { Icon: FlaskConical, title: "Whipped, Never Melted",              desc: "Our proprietary cold-whipping process preserves nutrients that heat destroys. The result is a texture that absorbs instantly." },
  { Icon: Wheat,        title: "Grass-Fed, Pasture-Raised Tallow",   desc: "Sourced from regenerative California ranches. Rich in vitamins A, D, E, and K in forms your skin can actually use." },
  { Icon: Droplet,      title: "Raw Honey and Beeswax",              desc: "Naturally antibacterial and deeply hydrating. Works with your skin's own moisture barrier instead of sitting on top of it." },
  { Icon: Ban,          title: "Zero Synthetics. Zero Fillers.",     desc: "No parabens, no fragrance oils, no preservatives, no water. Every jar is 100% active, whole-food ingredients." },
];

// Brand-neutral differentiators for Long Form v2 — category-agnostic copy used
// before AI-generated features populate. v1 never references this.
const DIFFERENTIATORS_V2 = [
  { Icon: FlaskConical, title: "Thoughtfully Made",        desc: "A careful process that protects what matters, so the result actually performs the way it should." },
  { Icon: Wheat,        title: "Quality Materials",        desc: "Chosen from partners we trust for how well they hold up over time, not how cheap they are." },
  { Icon: Droplet,      title: "Does Real Work",           desc: "Built to function, not just to look good. Every part earns its place." },
  { Icon: Ban,          title: "No Filler. No Shortcuts.", desc: "Nothing padded in to cut costs. Just the things that actually make it better." },
];

function WhatMakesDifferentSection({
  differentiator,
  differentiatorLoading = false,
  images,
  imagesLoading = false,
  onRetry,
}: {
  differentiator?: DifferentiatorCopy;
  differentiatorLoading?: boolean;
  images?: LpImgSlot[];
  imagesLoading?: boolean;
  onRetry?: (slotKey: string) => void;
}) {
  const v2 = useLpV2();
  const baseDiff = v2 ? DIFFERENTIATORS_V2 : DIFFERENTIATORS;
  const eyebrow = pick(differentiator?.section_eyebrow) || "What Makes This Different";
  const aiHeadline = pick(differentiator?.section_headline);
  const aiFeatureEntries = (differentiator?.features ?? [])
    .map((f, idx) => ({ f, idx }))
    .filter(({ f }) => pick(f?.title) || pick(f?.body));
  const aiFeatures = aiFeatureEntries.map((e) => e.f);
  const features = aiFeatureEntries.length
    ? aiFeatureEntries.map(({ f, idx }, i) => ({
        Icon: baseDiff[i % baseDiff.length].Icon,
        title: pick(f?.title) || baseDiff[i % baseDiff.length].title,
        titleRaw: pick(f?.title),
        titleFallback: baseDiff[i % baseDiff.length].title,
        desc: pick(f?.body) || baseDiff[i % baseDiff.length].desc,
        descRaw: pick(f?.body),
        descFallback: baseDiff[i % baseDiff.length].desc,
        storedIndex: idx,
      }))
    : baseDiff.map((f) => ({ Icon: f.Icon, title: f.title, titleRaw: "", titleFallback: f.title, desc: f.desc, descRaw: "", descFallback: f.desc, storedIndex: -1 }));
  const trustBadge = pick(differentiator?.trust_badge) || (v2 ? "Every order is made with care and shipped within days." : "Every batch is hand-poured in Temecula, CA and shipped within days of production.");
  const textureSlot = resolveImgSlot(images, "material-texture", imagesLoading);
  const isAi = !!(aiHeadline || (aiFeatures && aiFeatures.length) || pick(differentiator?.section_eyebrow) || pick(differentiator?.trust_badge));
  return (
    <section style={{ padding: "64px 16px", backgroundColor: "#FDFCF8" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <p style={{ position: "relative" as const, fontSize: 10, fontWeight: 900, letterSpacing: "0.22em", color: "#9A8E80", textTransform: "uppercase" as const, textAlign: "center" as const, marginBottom: 8 }}><EditableText as="span" elementKey="differentiator:eyebrow" path={["differentiator", "section_eyebrow"]} value={pick(differentiator?.section_eyebrow)} placeholder="What Makes This Different" />{" "}<SourceDot source={isAi ? "workflow" : "placeholder"} /></p>
        {aiHeadline ? (
          <EditableText as="h2" elementKey="differentiator:headline" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic" as const, fontSize: 28, color: "var(--color-brand-navyaccent)", textAlign: "center" as const, lineHeight: 1.25, marginBottom: 24 }} path={["differentiator", "section_headline"]} value={aiHeadline} placeholder="" multiline />
        ) : (
          <EditableText as="h2" elementKey="differentiator:headline" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic" as const, fontSize: 28, color: "var(--color-brand-navyaccent)", textAlign: "center" as const, lineHeight: 1.25, marginBottom: 24, whiteSpace: "pre-line" as const }} path={["differentiator", "section_headline"]} value={aiHeadline} placeholder={v2 ? "Not All Products\nAre Created Equal." : "Not All Skincare\nIs Created Equal."} multiline />
        )}
        <EditableElement elementKey="differentiator:texture-image" toolbarSide="left" testId="element-differentiator-texture-image">
          <div className="relative group/lpimg" style={{ borderRadius: 14, overflow: "hidden", border: "1px solid #E8E0D4", marginBottom: 28 }}>
            <SlotImage slot={textureSlot} fallbackPath={v2 ? "texture image" : "/honey-texture.jpg"} alt={v2 ? "Material texture" : "Ingredient texture"} rounded={false} style={{ height: 220 }} onRetry={onRetry} overrideKey="proofImages:material-texture" regenKey="material-texture" />
          </div>
        </EditableElement>
        <div style={{ display: "flex", flexDirection: "column" as const, gap: 20 }}>
          {features.map(item => (
            <div key={item.title} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: "var(--color-brand-cream)", border: "1px solid #E8E0D4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <item.Icon className="w-5 h-5" style={{ color: "var(--color-brand-navyaccent)" }} strokeWidth={2} />
              </div>
              <div>
                {item.storedIndex >= 0
                  ? <EditableText as="p" style={{ fontSize: 14, fontWeight: 900, color: "var(--color-brand-deepnavy)", marginBottom: 3 }} path={["differentiator", "features", item.storedIndex, "title"]} value={item.titleRaw} placeholder={item.titleFallback} multiline />
                  : <p style={{ fontSize: 14, fontWeight: 900, color: "var(--color-brand-deepnavy)", marginBottom: 3 }}>{item.title}</p>}
                {item.storedIndex >= 0
                  ? <EditableText as="p" style={{ fontSize: 13, color: "#5A554F", lineHeight: 1.55, margin: 0 }} path={["differentiator", "features", item.storedIndex, "body"]} value={item.descRaw} placeholder={item.descFallback} multiline />
                  : <p style={{ fontSize: 13, color: "#5A554F", lineHeight: 1.55, margin: 0 }}>{item.desc}</p>}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 28, padding: "14px 18px", backgroundColor: "#E8F3ED", borderRadius: 10, border: "1px solid #C5E1D4", textAlign: "center" as const }}>
          <EditableText as="p" path={["differentiator", "trust_badge"]} value={pick(differentiator?.trust_badge)} placeholder={v2 ? "Every order is made with care and shipped within days." : "Every batch is hand-poured in Temecula, CA and shipped within days of production."} multiline style={{ fontSize: 12, fontWeight: 700, color: "var(--color-brand-greenaccent)", margin: 0 }} />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   S15 – REVIEWS
───────────────────────────────────────────── */

export function ReviewsSection({ reviews, loading = false, reviewDisplay }: { reviews?: ReviewsCopy; loading?: boolean; reviewDisplay?: LpReviewDisplay }) {
  const [visibleCount, setVisibleCount] = useState(5);
  const v2 = useLpV2();
  const aiReviewEntries = (reviews?.reviews ?? [])
    .map((rv, idx) => ({ rv, idx }))
    .filter(({ rv }) => pick(rv?.quote) && pick(rv?.name) && !rv?.hidden);
  const showReviewsSpinner = loading && !aiReviewEntries.length;
  // v2 carries NO brand-specific hardcoded reviews. Card source priority:
  // AI reviews → real per-product reviews (reviewDisplay.allReviews) → empty.
  // v1 keeps its hardcoded reference reviews so its output stays byte-identical.
  const realReviews = reviewDisplay?.allReviews ?? [];
  const hardcodedReviews = v2
    ? []
    : [
        ...BALM_REVIEWS.map(r => ({ ...r, label: "Tallow Balm" })),
        ...EYE_CREAM_REVIEWS.map(r => ({ ...r, label: "Revival Eye Cream" })),
        ...LIP_BALM_REVIEWS.map(r => ({ ...r, label: "Lip Balm" })),
      ];
  const allReviews = aiReviewEntries.length
    ? aiReviewEntries.map(({ rv, idx }) => ({
        name: pick(rv?.name),
        quote: pick(rv?.quote),
        scent: undefined as string | undefined,
        label: pick(rv?.variant) || "Verified",
        storedIndex: idx,
        hidden: rv?.hidden,
      }))
    : v2
      ? realReviews.map((rv) => ({
          name: rv.reviewer || "Verified Buyer",
          quote: rv.reviewText,
          scent: undefined as string | undefined,
          label: "Verified",
          storedIndex: -1,
          hidden: undefined as boolean | undefined,
        }))
      : hardcodedReviews.map((r) => ({ ...r, storedIndex: -1, hidden: undefined as boolean | undefined }));
  const shown = allReviews.slice(0, visibleCount);
  // Aggregate rating/count is real-data only. v1 always renders it (its
  // reviewDisplay carries the hardcoded 4.8 / 4,000+ fallback — byte-identical);
  // v2 renders it ONLY when real reviews exist, never fabricated numbers.
  const showRatingLine = v2 ? !!reviewDisplay?.hasReviews : true;
  const isEmptyV2 = v2 && allReviews.length === 0;
  const avatarColors = ["#E8927C", "#7BBFB2", "#A78BCA", "#E0A45E", "#8CB4D9"];

  return (
    <section className="py-24 px-4 sm:px-6 bg-white border-b border-brand-lightborder">
      <div className="max-w-[520px] mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
          <div>
            <EditableText
              as="h2"
              elementKey="reviews:headline"
              className="text-3xl font-black text-brand-navy uppercase tracking-tight mb-2"
              style={{ whiteSpace: "pre-line" }}
              path={["reviews", "section_headline"]}
              value={(reviews as any)?.section_headline || (v2 ? "Real Reviews.\nReal Results.\nReal Customers." : "Real Talk.\nReal Ingredients.\nReal Customers.")}
              placeholder={v2 ? "Real Reviews. Real Results. Real Customers." : "Real Talk. Real Ingredients. Real Customers."}
              multiline
            />
            {showRatingLine && (
              <div className="relative flex items-center gap-2">
                <span className="font-black text-xl text-brand-navy">{reviewDisplay?.ratingText ?? "4.8"}</span>
                <span className="text-yellow-400 tracking-wider">★★★★★</span>
                <span className="text-sm font-medium text-brand-muted ml-2">Based on {reviewDisplay?.reviewCountText ?? "4,000+"} reviews</span>
                {showReviewsSpinner && <Loader2 className="h-4 w-4 animate-spin ml-1" style={{ color: "var(--color-brand-navyaccent)", opacity: 0.5 }} data-testid="loading-reviews" />}
                <SourceDot source={aiReviewEntries.length ? "workflow" : "placeholder"} className="ml-1" />
              </div>
            )}
          </div>
        </div>

        {isEmptyV2 ? (
          <div className="py-12 text-center" data-testid="reviews-empty-v2">
            {showReviewsSpinner
              ? <Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: "var(--color-brand-navyaccent)", opacity: 0.5 }} data-testid="loading-reviews" />
              : <p className="text-sm font-medium text-brand-muted">No reviews yet.</p>}
          </div>
        ) : (
        <>
        <div className="flex flex-col" style={{ borderTop: "1px solid #EDE8E0" }}>
          {shown.map((r, i) => (
            <motion.div key={r.name + r.label} className="py-5" style={{ borderBottom: "1px solid #EDE8E0" }} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: Math.min((i % 5) * 0.04, 0.2) }}>
              <div className="flex items-start justify-between mb-2">
                <span className="text-base tracking-widest" style={{ color: "#F5A623" }}>★★★★★</span>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--color-brand-coral)" }}>{r.label}{r.scent ? ` · ${r.scent}` : ""}</span>
              </div>
              <p className="text-sm leading-relaxed mb-4" style={{ color: "#2A2A2A" }}>{renderBold(r.quote)}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white shrink-0" style={{ backgroundColor: avatarColors[i % 5] }}>
                    {r.name[0]}
                  </div>
                  <span className="font-semibold text-sm" style={{ color: "var(--color-brand-deepnavy)" }}>{r.name}</span>
                </div>
                <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--color-brand-greenaccent)" }}>
                  <CheckCircle2 size={12} />
                  <span>Verified Purchase</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {visibleCount < allReviews.length && (
          <div style={{ textAlign: "center" as const, marginTop: 24 }}>
            <button onClick={() => setVisibleCount(prev => prev + 5)} style={{ background: "none", border: "2px solid var(--color-brand-navyaccent)", color: "var(--color-brand-navyaccent)", fontWeight: 700, fontSize: 13, padding: "10px 28px", borderRadius: 24, cursor: "pointer", textTransform: "uppercase" as const, letterSpacing: "0.08em" }}>
              View More Reviews
            </button>
            <p style={{ fontSize: 11, color: "#999", marginTop: 8 }}>Showing {shown.length} of {allReviews.length} reviews</p>
          </div>
        )}
        </>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   FINAL CTA BUY BOX
───────────────────────────────────────────── */

function FinalCTASection({ ctaHeadline, loading = false, finalCta, finalCtaLoading = false, receipt, bundleImage, bundleProductPhotos, reviewDisplay, checkoutUrl }: { ctaHeadline?: string; loading?: boolean; finalCta?: FinalCtaCopy; finalCtaLoading?: boolean; receipt?: LpReceipt; bundleImage?: string | null; bundleProductPhotos?: string[]; reviewDisplay?: LpReviewDisplay; checkoutUrl?: string | null }) {
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  const v2 = useLpV2();
  const finalCtaOverride = useLpMediaOverride("product:final-cta");
  const finalCtaImage = finalCtaOverride ?? bundleImage;
  const finalItems = r.lineItems.map((item) => ({ name: item.name, desc: item.desc ?? "", retail: item.priceLabel }));

  const HARDCODED_BADGES = [
    { Icon: Lock,        label: "Secure\nCheckout" },
    { Icon: Home,        label: v2 ? "Ships\nFast" : "Ships from\nCalifornia" },
    { Icon: ShieldCheck, label: "45-Day\nGuarantee" },
    { Icon: Sparkles,    label: "Made\nFresh" },
  ];
  const aiBadges = finalCta?.trust_badges?.filter((b) => pick(b?.label));
  const TRUST_BADGES = aiBadges && aiBadges.length
    ? aiBadges.map((b, i) => ({ Icon: HARDCODED_BADGES[i % HARDCODED_BADGES.length].Icon, label: pick(b?.label) }))
    : HARDCODED_BADGES;

  const eyebrow = pick(finalCta?.section_eyebrow) || (v2 ? "Limited Time Offer" : "Limited Ranch Special");
  const aiHeadline = pick(finalCta?.section_headline);
  const subheadlineFallback = r.hasDiscount && r.discountPercent
    ? (v2 ? `A limited-time offer at ${r.discountPercent}% off before this deal ends.` : `No chemicals. No fillers. No shortcuts. Small-batch skincare crafted on a California ranch, at ${r.discountPercent}% off before this offer ends.`)
    : (v2 ? "A limited-time offer you don't want to miss." : "No chemicals. No fillers. No shortcuts. Small-batch skincare crafted on a California ranch.");
  const subheadline = pick(finalCta?.section_subheadline) || subheadlineFallback;
  const bundleLabel = pick(finalCta?.bundle_label) || r.bundleLabel;
  const savingsBadgeFallback = r.hasDiscount && r.discountPercent ? `SAVE ${r.discountPercent}%` : "";
  const savingsBadge = pick(finalCta?.savings_badge) || savingsBadgeFallback;
  const ctaButton = pick(finalCta?.cta_button) || r.ctaText;
  const ctaSubtextFallback = `Free shipping + ${r.guaranteeDays}-day money-back guarantee`;
  const ctaSubtext = pick(finalCta?.cta_subtext) || ctaSubtextFallback;
  const socialProofFallback = v2
    ? (reviewDisplay?.hasReviews ? `Rated by ${reviewDisplay.reviewCountText} real customers` : "")
    : `Rated by ${reviewDisplay?.reviewCountText ?? "4,000+"} real customers`;
  const socialProof = pick(finalCta?.social_proof_line) || socialProofFallback;
  const isAi = !!(aiHeadline || pick(ctaHeadline) || (aiBadges && aiBadges.length) || pick(finalCta?.section_eyebrow) || pick(finalCta?.section_subheadline) || pick(finalCta?.bundle_label) || pick(finalCta?.savings_badge) || pick(finalCta?.cta_button) || pick(finalCta?.cta_subtext) || pick(finalCta?.social_proof_line));

  return (
    <section style={{ padding: "56px 16px 48px", backgroundColor: "var(--color-brand-cream)", borderTop: "2px solid rgba(27,58,107,0.08)" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <p style={{ position: "relative" as const, fontSize: 10, fontWeight: 900, letterSpacing: "0.22em", color: "#9A8E80", textTransform: "uppercase" as const, textAlign: "center" as const, marginBottom: 10 }}><EditableText as="span" elementKey="finalCta:eyebrow" path={["finalCta", "section_eyebrow"]} value={pick(finalCta?.section_eyebrow)} placeholder={v2 ? "Limited Time Offer" : "Limited Ranch Special"} />{" "}<SourceDot source={isAi ? "workflow" : "placeholder"} /></p>
        <h2 style={{ fontSize: 30, fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic" as const, color: "var(--color-brand-navyaccent)", textAlign: "center" as const, lineHeight: 1.2, marginBottom: 8 }} data-testid="text-final-cta-headline">{ctaHeadline?.trim() ? ctaHeadline.trim() : aiHeadline ? <EditableText as="span" elementKey="finalCta:headline" path={["finalCta", "section_headline"]} value={aiHeadline} placeholder="" multiline /> : (loading || finalCtaLoading) ? <Loader2 className="inline-block h-6 w-6 animate-spin" style={{ color: "var(--color-brand-navyaccent)", opacity: 0.5 }} /> : <EditableText as="span" elementKey="finalCta:headline" style={{ whiteSpace: "pre-line" as const }} path={["finalCta", "section_headline"]} value={aiHeadline} placeholder={v2 ? "You Deserve\nthe Real Thing." : "Your Skin Deserves\nthe Real Thing."} multiline />}</h2>
        <EditableText as="p" elementKey="finalCta:subheadline" style={{ fontSize: 14, color: "#5A554F", textAlign: "center" as const, lineHeight: 1.65, maxWidth: 360, margin: "0 auto 24px" }} path={["finalCta", "section_subheadline"]} value={pick(finalCta?.section_subheadline)} placeholder={subheadlineFallback} multiline />

        <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5 }} style={{ marginBottom: 20 }}>
          <EditableElement elementKey="finalCta:bundle-image" toolbarSide="left" testId="element-finalcta-bundle-image">
            <div className="group/lpimg relative" style={{ borderRadius: 14, overflow: "hidden", border: "2px solid #D8CFC3", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
              <LpImage src={finalCtaImage} alt="Product bundle" path={v2 ? "product bundle photo" : "/bundle-flatlay.jpeg"} rounded={false} objectFit="contain" style={{ height: 240, backgroundColor: "#FAF6EE" }} />
              <LpImageActions slotKey="product:final-cta" productPhotos={bundleProductPhotos} revertLabel="Use default" revertTitle="Use the default product photo" />
            </div>
          </EditableElement>
        </motion.div>

        {/* Final CTA buy box — whole card is one hide/delete unit */}
        <EditableElement elementKey="finalCta:buy-box" testId="element-finalcta-buy-box">
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5 }} style={{ background: "var(--color-brand-card)", borderRadius: 16, border: "2px solid #D8CFC3", overflow: "hidden", boxShadow: "0 8px 30px rgba(0,0,0,0.08)", marginBottom: 20 }}>
          <div style={{ background: "linear-gradient(135deg, var(--color-brand-navyaccent) 0%, var(--color-brand-deepnavy) 100%)", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <EditableText as="span" style={{ color: "var(--color-brand-cream)", fontWeight: 900, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase" as const }} path={["finalCta", "bundle_label"]} value={pick(finalCta?.bundle_label)} placeholder={r.bundleLabel} />
            {savingsBadge && <EditableText as="span" style={{ backgroundColor: "var(--color-brand-coral)", color: "#FFFFFF", fontSize: 10, fontWeight: 900, padding: "3px 10px", borderRadius: 20 }} path={["finalCta", "savings_badge"]} value={pick(finalCta?.savings_badge)} placeholder={savingsBadgeFallback} />}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "0 6px", backgroundColor: "var(--color-brand-card)" }}>
            {Array.from({ length: 22 }).map((_, i) => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "var(--color-brand-cream)", marginTop: -4 }} />)}
          </div>
          <div style={{ padding: "8px 20px 0" }}>
            {finalItems.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.08 }} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 0", borderBottom: i < finalItems.length - 1 ? "1px dashed #E8E0D4" : "none" }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: "var(--color-brand-deepnavy)", margin: 0, lineHeight: 1.3 }}>{item.name}</p>
                  {item.desc && <p style={{ fontSize: 11, color: "#9A8E80", margin: "2px 0 0", lineHeight: 1.3 }}>{item.desc}</p>}
                </div>
                {item.retail && <span style={{ fontSize: 13, fontWeight: 700, color: "#C0392B", textDecoration: r.hasDiscount ? "line-through" : "none", flexShrink: 0, marginLeft: 12, marginTop: 2 }}>{item.retail}</span>}
              </motion.div>
            ))}
          </div>
          <div style={{ margin: "0 20px", padding: "10px 0", borderTop: "1px dashed #E8E0D4", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <EditableText as="span" style={{ fontSize: 13, fontWeight: 600, color: "var(--color-brand-deepnavy)" }} path={["finalCta", "shipping_label"]} value={(finalCta as any)?.shipping_label} placeholder="Free U.S. Shipping" />
            <span style={{ fontSize: 12, fontWeight: 800, color: "var(--color-brand-greenaccent)" }}>FREE</span>
          </div>
          {r.hasDiscount && (
            <div style={{ margin: "0 20px", padding: "12px 0 6px", borderTop: "2px solid var(--color-brand-deepnavy)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <EditableText as="span" style={{ fontSize: 11, fontWeight: 800, color: "#6B6560", letterSpacing: "0.06em", textTransform: "uppercase" as const }} path={["finalCta", "retail_total_label"]} value={(finalCta as any)?.retail_total_label} placeholder="Retail Total" />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#6B6560", textDecoration: "line-through" }}>{r.retailLabel}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: "var(--color-brand-greenaccent)", letterSpacing: "0.06em", textTransform: "uppercase" as const }}>{r.discountRowLabel}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "var(--color-brand-greenaccent)" }}>{r.discountAmountLabel}</span>
              </div>
            </div>
          )}
          <div style={{ margin: "0 20px", padding: "14px 0", borderTop: "2px dashed #D8CFC3", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <EditableText as="span" style={{ fontSize: 11, fontWeight: 900, color: "var(--color-brand-deepnavy)", letterSpacing: "0.12em", textTransform: "uppercase" as const }} path={["finalCta", "you_pay_label"]} value={(finalCta as any)?.you_pay_label} placeholder="You Pay Today" />
            <span style={{ fontSize: 36, fontWeight: 900, color: "var(--color-brand-navyaccent)", lineHeight: 1 }}>{r.priceTodayLabel}</span>
          </div>
          <div style={{ padding: "0 20px 20px" }}>
            <motion.a href={v2 ? (checkoutUrl || undefined) : CHECKOUT_URL} target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} style={{ display: "block", width: "100%", padding: "16px 0", background: v2 ? "var(--color-brand-greenaccent)" : "linear-gradient(135deg, var(--color-brand-greenaccent) 0%, #1F5E3C 100%)", color: "#FFFFFF", fontSize: 17, fontWeight: 900, textAlign: "center" as const, borderRadius: 12, textDecoration: "none", boxSizing: "border-box" as const, letterSpacing: "0.06em", textTransform: "uppercase" as const, boxShadow: v2 ? "0 6px 24px rgba(0,0,0,0.28)" : "0 6px 24px rgba(45,122,79,0.35)" }}>
              <EditableText as="span" noHideDelete path={["finalCta", "cta_button"]} value={pick(finalCta?.cta_button)} placeholder={r.ctaText} />
              <EditableText as="span" noHideDelete style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginTop: 3, textTransform: "none" as const, letterSpacing: "0.02em" }} path={["finalCta", "cta_subtext"]} value={pick(finalCta?.cta_subtext)} placeholder={ctaSubtextFallback} />
            </motion.a>
          </div>
        </motion.div>
        </EditableElement>

        {/* Trust badges */}
        <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 16 }}>
          {TRUST_BADGES.map((b, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 4 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "rgba(27,58,107,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <b.Icon className="w-4 h-4" style={{ color: "var(--color-brand-navyaccent)" }} strokeWidth={2} />
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, color: "#6B6560", textAlign: "center" as const, lineHeight: 1.3, whiteSpace: "pre-line" as const }}>{b.label}</span>
            </div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} style={{ textAlign: "center" as const, marginTop: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 4 }}>
            {Array.from({ length: 5 }).map((_, i) => <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="var(--color-brand-goldaccent)" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>)}
            <span style={{ fontSize: 12, fontWeight: 800, color: "var(--color-brand-deepnavy)", marginLeft: 4 }}>{reviewDisplay?.ratingText ?? "4.8"}</span>
          </div>
          <EditableText as="p" style={{ fontSize: 11, color: "#9A8E80", fontWeight: 600, margin: 0 }} path={["finalCta", "social_proof_line"]} value={pick(finalCta?.social_proof_line)} placeholder={socialProofFallback} />
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */

// Pull a real social URL for a given platform out of the flexible
// `brand.socialProfiles` jsonb (string / array / object shapes). Only returns a
// genuine http(s) URL so the footer icons stay decorative when no real link
// exists — we never fabricate a destination from a bare handle.
function extractSocialUrl(socialProfiles: unknown, keys: string[]): string | null {
  if (!socialProfiles) return null;
  const matches = (s: string) => keys.some((k) => s.toLowerCase().includes(k));
  const toUrl = (raw: unknown): string | null => {
    if (typeof raw !== "string") return null;
    const v = raw.trim();
    return /^https?:\/\//i.test(v) ? v : null;
  };
  if (Array.isArray(socialProfiles)) {
    for (const entry of socialProfiles) {
      if (entry && typeof entry === "object") {
        const platform = String((entry as any).platform ?? (entry as any).name ?? (entry as any).type ?? "");
        const url = (entry as any).url ?? (entry as any).link ?? (entry as any).href ?? (entry as any).handle;
        if (matches(platform) || matches(String(url ?? ""))) {
          const u = toUrl(url);
          if (u) return u;
        }
      } else if (typeof entry === "string" && matches(entry)) {
        const u = toUrl(entry);
        if (u) return u;
      }
    }
  } else if (typeof socialProfiles === "object") {
    for (const [k, val] of Object.entries(socialProfiles as Record<string, unknown>)) {
      if (val == null) continue;
      if (matches(k) || matches(String(val))) {
        const u = toUrl(val);
        if (u) return u;
      }
    }
  } else if (typeof socialProfiles === "string" && matches(socialProfiles)) {
    const u = toUrl(socialProfiles);
    if (u) return u;
  }
  return null;
}

function Footer({ logoUrl, brandName, brandUrl, brandSummary, socialProfiles, footer }: { logoUrl?: string | null; brandName?: string | null; brandUrl?: string | null; brandSummary?: string | null; socialProfiles?: unknown; footer?: FooterCopy }) {
  const editor = useLpEditor();
  const v2 = useLpV2();
  const home = brandUrl?.trim() || (v2 ? "" : "https://thepapabear.com");
  const socials: { label: string; url: string | null }[] = [
    { label: "IG", url: extractSocialUrl(socialProfiles, ["instagram"]) },
    { label: "TT", url: extractSocialUrl(socialProfiles, ["tiktok"]) },
    { label: "FB", url: extractSocialUrl(socialProfiles, ["facebook", "fb.com"]) },
  ];
  const exploreLinks: Array<[string, string]> = v2
    ? [["Shop All", home], ["Our Story", home], ["Ingredients", home], ["Reviews", home]]
    : [["Shop All", "https://thepapabear.com/collections/shop-all"], ["Our Story", "https://thepapabear.com/pages/about-papabear"], ["Ingredients", home], ["Reviews", home]];
  const supportLinks: Array<[string, string]> = v2
    ? [["Contact Us", home], ["FAQ", home], ["Terms of Service", home], ["Privacy Policy", home]]
    : [["Contact Us", "mailto:support@thepapabear.com"], ["FAQ", home], ["Terms of Service", home], ["Privacy Policy", home]];
  return (
    <footer className={`${v2 ? "bg-[var(--color-brand-deepnavy)]" : "bg-brand-deepnavy"} text-white pt-20 pb-10 px-4 sm:px-6`}>
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
        <div className="lg:col-span-2">
          <EditableElement elementKey="footer:logo-image" toolbarSide="left" testId="element-footer-logo-image" className="inline-block">
            <LpImage src={logoUrl} alt="Brand logo" path={v2 ? "brand logo" : "/logo-badge.png"} objectFit="contain" style={{ width: 64, height: 64, minHeight: 64, borderRadius: "50%", padding: 0, marginBottom: 24 }} />
          </EditableElement>
          <EditableText as="p" className="text-2xl font-serif italic text-white/90 mb-4" path={["footer", "tagline"]} value={footer?.tagline} placeholder="Made by Us, For Yours." multiline />
          <EditableText as="p" className="text-white/60 text-sm max-w-sm leading-relaxed mb-6" path={["footer", "description"]} value={footer?.description} placeholder={brandSummary?.trim() || (v2 ? "Crafted with care using the finest ingredients we can find." : "Ranch-crafted in Murrieta, California using 100% grass-fed beef tallow and the cleanest ingredients on earth.")} multiline />
          <div className="flex gap-4">
            {socials.map((s, i) => {
              const socialUrl = (footer?.social_links?.[i]?.url ?? s.url ?? "") as string;
              const cls = `w-10 h-10 rounded-full bg-white/5 flex items-center justify-center ${v2 ? "hover:bg-[var(--color-brand-coral)]" : "hover:bg-brand-coral"} transition-colors cursor-pointer text-sm`;
              if (editor) {
                return (
                  <div key={s.label} className="group/lplink relative inline-flex">
                    {editor.onEditLinkUrl && <LinkUrlPopover path={["footer", "social_links", i, "url"]} url={socialUrl} testId={`footer-social-${i}`} />}
                    <a href={socialUrl || undefined} onClick={(e) => e.preventDefault()} className={cls}>{s.label}</a>
                  </div>
                );
              }
              return socialUrl
                ? <a key={s.label} href={socialUrl} target="_blank" rel="noopener noreferrer" className={cls}>{s.label}</a>
                : <div key={s.label} className={cls}>{s.label}</div>;
            })}
          </div>
        </div>
        <div>
          <EditableText as="h4" className="font-bold uppercase tracking-widest text-sm mb-6 text-white/80" path={["footer", "explore_label"]} value={footer?.explore_label} placeholder="Explore" />
          <ul className="space-y-4">
            {exploreLinks.map(([label, href], i) => {
              const linkUrl = (footer?.explore_links?.[i]?.url ?? href) as string;
              return (
                <EditableElement as="li" key={label} elementKey={`footer:explore-link-${i}`} testId={`element-footer-explore-${i}`}>
                  <span className="group/lplink relative inline-flex">
                    {editor?.onEditLinkUrl && <LinkUrlPopover path={["footer", "explore_links", i, "url"]} url={linkUrl} testId={`footer-explore-${i}`} />}
                    <a href={linkUrl || undefined} onClick={editor ? (e) => e.preventDefault() : undefined} className="text-white/60 hover:text-white transition-colors font-medium">
                      <EditableText as="span" noHideDelete path={["footer", "explore_links", i, "label"]} value={footer?.explore_links?.[i]?.label} placeholder={label} />
                    </a>
                  </span>
                </EditableElement>
              );
            })}
          </ul>
        </div>
        <div>
          <EditableText as="h4" className="font-bold uppercase tracking-widest text-sm mb-6 text-white/80" path={["footer", "support_label"]} value={footer?.support_label} placeholder="Support" />
          <ul className="space-y-4">
            {supportLinks.map(([label, href], i) => {
              const linkUrl = (footer?.support_links?.[i]?.url ?? href) as string;
              return (
                <EditableElement as="li" key={label} elementKey={`footer:support-link-${i}`} testId={`element-footer-support-${i}`}>
                  <span className="group/lplink relative inline-flex">
                    {editor?.onEditLinkUrl && <LinkUrlPopover path={["footer", "support_links", i, "url"]} url={linkUrl} testId={`footer-support-${i}`} />}
                    <a href={linkUrl || undefined} onClick={editor ? (e) => e.preventDefault() : undefined} className="text-white/60 hover:text-white transition-colors font-medium">
                      <EditableText as="span" noHideDelete path={["footer", "support_links", i, "label"]} value={footer?.support_links?.[i]?.label} placeholder={label} />
                    </a>
                  </span>
                </EditableElement>
              );
            })}
          </ul>
        </div>
      </div>
      <div className="max-w-6xl mx-auto pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/40 font-medium">
        <EditableText as="p" path={["footer", "copyright"]} value={footer?.copyright} placeholder={`© ${new Date().getFullYear()} ${brandName?.trim() || (v2 ? "Your Brand" : "Papabear Naturals")}. All rights reserved.`} />
        <EditableText as="p" path={["footer", "bottom_note"]} value={footer?.bottom_note} placeholder={v2 ? "Made with care." : "Designed for real skin."} />
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────
   MAIN EXPORT — compose all sections
───────────────────────────────────────────── */

// The long-form page's sub-section blocks, in canonical render order. This
// order reproduces the legacy composite exactly; the renderer drives the
// actual on-page order from the persisted sections array.
export const REMAINING_SECTION_ORDER = [
  "timeline",
  "membership",
  "proof",
  "alternatives",
  "philosophy",
  "differentiator",
  "faq",
  "reviews",
  "finalCta",
  "footer",
] as const;

export type RemainingSectionType = (typeof REMAINING_SECTION_ORDER)[number];

export function RemainingPageSections({
  receipt,
  logoUrl,
  bundleImage,
  bundleProductPhotos,
  reviewDisplay,
  quotes,
  quotesLoading = false,
  timeline,
  timelineLoading = false,
  membership,
  membershipLoading = false,
  brandName,
  brandUrl,
  brandSummary,
  socialProfiles,
  alternatives,
  alternativesLoading = false,
  alternativesImages,
  alternativesImagesLoading = false,
  philosophy,
  philosophyLoading = false,
  philosophyImages,
  philosophyImagesLoading = false,
  differentiator,
  differentiatorLoading = false,
  proofImages,
  proofImagesLoading = false,
  proof,
  proofLoading = false,
  checkoutUrl,
  faq,
  faqLoading = false,
  reviews,
  reviewsLoading = false,
  finalCta,
  finalCtaLoading = false,
  footer,
  proofPoints,
  onRetrySlot,
  section,
}: {
  receipt?: LpReceipt;
  // Real brand logo + product flat-lay image; each falls back to its dashed
  // placeholder when missing.
  logoUrl?: string | null;
  bundleImage?: string | null;
  // Full product photo gallery (first product) backing the CTA buy-box image
  // pickers — lets each CTA "choose from product photos" / upload / revert.
  bundleProductPhotos?: string[];
  // Aggregated star rating + review count, falling back to "4.8" / "4,000+".
  reviewDisplay?: LpReviewDisplay;
  quotes?: Record<string, { quote?: string; attribution?: string }>;
  // True while the lp-quote-copy workflow is still generating — child sections
  // show a spinner instead of the hardcoded fallback copy.
  quotesLoading?: boolean;
  timeline?: TimelineCopy;
  timelineLoading?: boolean;
  membership?: MembershipCopy;
  membershipLoading?: boolean;
  brandName?: string | null;
  // Real brand homepage URL, descriptive summary, and social profiles (jsonb)
  // used to wire the footer nav links, description paragraph, and social icons.
  // Each falls back to the current hardcoded literal when empty.
  brandUrl?: string | null;
  brandSummary?: string | null;
  socialProfiles?: unknown;
  alternatives?: AlternativesCopy;
  alternativesLoading?: boolean;
  alternativesImages?: LpImgSlot[];
  alternativesImagesLoading?: boolean;
  philosophy?: PhilosophyCopy;
  philosophyLoading?: boolean;
  philosophyImages?: LpImgSlot[];
  philosophyImagesLoading?: boolean;
  differentiator?: DifferentiatorCopy;
  differentiatorLoading?: boolean;
  proofImages?: LpImgSlot[];
  proofImagesLoading?: boolean;
  // Science / proof section copy (lp-proof-copy). Silent-fallback: missing
  // fields render nothing. checkoutUrl wires the section's buy box.
  proof?: ProofCopy;
  proofLoading?: boolean;
  checkoutUrl?: string | null;
  faq?: FaqCopy;
  faqLoading?: boolean;
  reviews?: ReviewsCopy;
  reviewsLoading?: boolean;
  finalCta?: FinalCtaCopy;
  finalCtaLoading?: boolean;
  // Per-page footer text overrides (tagline, nav labels/links, legal lines).
  footer?: FooterCopy;
  // Brand's own proof points (jsonb). Drives the trust-strip Marquee; when empty
  // the strip renders nothing rather than showing generic claims.
  proofPoints?: unknown;
  // Retry a single failed section image slot (in-app builder only). Undefined on
  // the public/published page so failed slots stay empty (no retry affordance).
  onRetrySlot?: (mergeKey: "alternativesImages" | "philosophyImages" | "proofImages", slotKey: string) => void;
  // When set, render ONLY this one sub-section block (plus any interstitial
  // dividers that travel with it). This is what makes the long-form page's
  // sub-sections individually dispatchable / reorderable by the renderer. When
  // omitted, the full composite renders in canonical order (legacy behavior).
  section?: RemainingSectionType;
}) {
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  const proofItems = parseProofPoints(proofPoints);
  const retryAlternatives = onRetrySlot ? (slotKey: string) => onRetrySlot("alternativesImages", slotKey) : undefined;
  const retryPhilosophy = onRetrySlot ? (slotKey: string) => onRetrySlot("philosophyImages", slotKey) : undefined;
  const retryProof = onRetrySlot ? (slotKey: string) => onRetrySlot("proofImages", slotKey) : undefined;

  // Each sub-section block, keyed by its section type. Interstitial dividers
  // (SpringSaleBanner / Marquee) travel WITH the anchor block they trail in the
  // legacy layout, so that whether we render the full composite (default order)
  // or a single dispatched block, the visual output is byte-identical to the
  // pre-decomposition page.
  const blocks: Record<RemainingSectionType, React.ReactNode> = {
    timeline: <TimelineSection timeline={timeline} loading={timelineLoading} receipt={r} checkoutUrl={checkoutUrl} />,
    membership: (
      <FreshnessSection
        valueReframe={quotes?.value_reframe?.quote}
        loading={quotesLoading}
        membership={membership}
        membershipLoading={membershipLoading}
        receipt={r}
        logoUrl={logoUrl}
        brandName={brandName}
      />
    ),
    proof: (
      <ScienceSection
        proof={proof}
        loading={proofLoading}
        images={proofImages}
        imagesLoading={proofImagesLoading}
        receipt={r}
        brandName={brandName}
        bundleImage={bundleImage}
        bundleProductPhotos={bundleProductPhotos}
        checkoutUrl={checkoutUrl}
        onRetry={retryProof}
      />
    ),
    alternatives: (
      <>
        <AlternativesSection
          closingChallenge={quotes?.closing_challenge?.quote}
          loading={quotesLoading}
          alternatives={alternatives}
          alternativesLoading={alternativesLoading}
          images={alternativesImages}
          imagesLoading={alternativesImagesLoading}
          receipt={r}
          bundleImage={bundleImage}
          bundleProductPhotos={bundleProductPhotos}
          brandName={brandName}
          checkoutUrl={checkoutUrl}
          onRetry={retryAlternatives}
        />
        <SpringSaleBanner receipt={r} />
      </>
    ),
    philosophy: (
      <>
        <FunctionalSkincareSection
          philosophy={philosophy}
          philosophyLoading={philosophyLoading}
          images={philosophyImages}
          imagesLoading={philosophyImagesLoading}
          onRetry={retryPhilosophy}
        />
        <Marquee items={proofItems} />
      </>
    ),
    differentiator: (
      <>
        <WhatMakesDifferentSection
          differentiator={differentiator}
          differentiatorLoading={differentiatorLoading}
          images={proofImages}
          imagesLoading={proofImagesLoading}
          onRetry={retryProof}
        />
        <Marquee items={proofItems} />
        <SpringSaleBanner receipt={r} />
      </>
    ),
    faq: <FAQ faqs={faq?.faqs} loading={faqLoading} />,
    reviews: <ReviewsSection reviews={reviews} loading={reviewsLoading} reviewDisplay={reviewDisplay} />,
    finalCta: (
      <FinalCTASection
        ctaHeadline={quotes?.cta_headline?.quote}
        loading={quotesLoading}
        finalCta={finalCta}
        finalCtaLoading={finalCtaLoading}
        receipt={r}
        bundleImage={bundleImage}
        bundleProductPhotos={bundleProductPhotos}
        reviewDisplay={reviewDisplay}
        checkoutUrl={checkoutUrl}
      />
    ),
    footer: (
      <Footer logoUrl={logoUrl} brandName={brandName} brandUrl={brandUrl} brandSummary={brandSummary} socialProfiles={socialProfiles} footer={footer} />
    ),
  };

  // Single-block dispatch: render only the requested sub-section.
  if (section) return <>{blocks[section]}</>;

  // Full composite (legacy): render every block in canonical order.
  return (
    <>
      {REMAINING_SECTION_ORDER.map((key) =>
        // Attach the key to each block's own root element rather than a
        // React.Fragment (see note in AlternativesSection): the dev
        // cartographer plugin injects `data-replit-metadata`, which a
        // Fragment rejects with a console warning.
        React.isValidElement(blocks[key])
          ? React.cloneElement(blocks[key] as React.ReactElement, { key })
          : null,
      )}
    </>
  );
}
